/**
 * Vendor Portal API Service
 * Handles all API calls for the vendor portal
 */
import axios from 'axios';
import type { AxiosInstance, AxiosResponse } from 'axios';
import type { PtAssessmentCheck } from '@/modules/business/maud-erp/display-devices/components/forms/DisplayerLicenseFields';
import type {
  LoginCredentials,
  SignupCredentials,
  AuthResponse,
  DashboardStats,
  Alert,
  ContractorWork,
  WorkDetails,
  Milestone,
  ContractorDocument,
  DocumentType,
  ProgressUpdate,
  FinancialSummary,
  Payment,
  Bill,
  Deduction,
  BankGuarantee,
  SubUser,
  ApiResponse,
  ContractorUser,
  ModulePermissions,
  CaptchaData,
  ThemeId,
  SignupMasters,
  OtpIssueResult
} from '../lib/types';

const BASE_URL = '/apiv1/ds-vendor';

// ---------------------------------------------------------------------------
// Session helpers
//
// The token carries its own expiry, but the portal used to treat "a string
// exists in localStorage" as "logged in". That left the UI looking live long
// after the session died, so the first button the vendor pressed returned 401
// and hard-redirected them to login — losing anything they had typed. These
// helpers let the app see the expiry before it fires a request.
// ---------------------------------------------------------------------------
const TOKEN_KEY = 'vendor_token';
const USER_KEY = 'vendor_user';
/** Fired whenever the cached user changes so mounted layouts can re-read it. */
export const USER_UPDATED_EVENT = 'vendor-user-updated';
const LOGIN_PATH = '/app/vendor-portal/login';
const REDIRECT_KEY = 'vendor_post_login_redirect';
const NOTICE_KEY = 'vendor_session_notice';

export type SessionEndReason = 'expired' | 'inactive';

/** Decode a JWT payload. Display/expiry use only — the server still verifies. */
const decodeJwt = (token: string): Record<string, any> | null => {
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const bin = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes));
  } catch {
    return null;
  }
};

/** Expiry of the stored token as epoch ms, or null when absent/unreadable. */
export const getTokenExpiryMs = (): number | null => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (!token) return null;
  const exp = Number(decodeJwt(token)?.exp);
  return Number.isFinite(exp) ? exp * 1000 : null;
};

/**
 * Is the stored token past its expiry? `skewSeconds` treats a token that is
 * about to lapse as already dead, so we don't fire a request guaranteed to 401.
 */
export const isTokenExpired = (skewSeconds = 0): boolean => {
  const expiryMs = getTokenExpiryMs();
  if (expiryMs === null) return false;   // no readable exp — let the server decide
  return Date.now() + skewSeconds * 1000 >= expiryMs;
};

/** A usable session = a token that exists and has not lapsed. */
export const isSessionValid = (): boolean =>
  !!localStorage.getItem(TOKEN_KEY) && !isTokenExpired();

/** Remember where the vendor was, so re-login can put them back there. */
export const rememberPostLoginRedirect = (path?: string): void => {
  try {
    const target = path ?? `${window.location.pathname}${window.location.search}`;
    if (target && !target.includes('/login')) sessionStorage.setItem(REDIRECT_KEY, target);
  } catch {
    /* private-mode storage — the redirect is a nicety, not a requirement */
  }
};

/**
 * Read-and-clear whatever the last session teardown left behind, so the login
 * page can explain itself ("your session expired") and send the vendor back to
 * the page they were on instead of dumping them on the dashboard.
 */
export const consumeSessionNotice = (): { reason: SessionEndReason | null; redirectTo: string | null } => {
  try {
    const reason = sessionStorage.getItem(NOTICE_KEY) as SessionEndReason | null;
    const redirectTo = sessionStorage.getItem(REDIRECT_KEY);
    sessionStorage.removeItem(NOTICE_KEY);
    sessionStorage.removeItem(REDIRECT_KEY);
    return { reason, redirectTo };
  } catch {
    return { reason: null, redirectTo: null };
  }
};

// ---------------------------------------------------------------------------
// Session keep-alive
//
// The server slips a fresh token into a response header once the current one is
// past half its life — but that only helps if a request happens inside that
// window. A vendor who opens the portal and then leaves the tab idle makes no
// such request, so the token quietly lapses and the next thing they click
// (usually Device Permission) 401s them straight to login.
//
// So the portal renews the token itself: on a timer, and whenever the tab comes
// back to the foreground. While a tab is open the session no longer expires
// under the vendor. This renews an *already-valid* session — it cannot revive
// an expired one, so it must run often enough to always land before expiry.
// ---------------------------------------------------------------------------
const KEEPALIVE_INTERVAL_MS = 5 * 60 * 1000;   // 5 min — well inside any expiry
const RENEW_WHEN_LIFE_LEFT = 0.5;              // renew past half the token's life

let keepAliveTimer: ReturnType<typeof setInterval> | null = null;
let renewInFlight: Promise<void> | null = null;

/** Should we renew yet? True once the token is past half its life. */
const needsRenewal = (): boolean => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (!token) return false;
  const claims = decodeJwt(token);
  const exp = Number(claims?.exp);
  const iat = Number(claims?.iat);
  if (!Number.isFinite(exp)) return false;
  if (!Number.isFinite(iat)) return true;   // no issue time — renew and move on
  const lifeMs = (exp - iat) * 1000;
  return Date.now() >= exp * 1000 - lifeMs * RENEW_WHEN_LIFE_LEFT;
};

/** Renew now if the token is due. Concurrent calls share one request. */
export const renewSessionIfDue = async (): Promise<void> => {
  if (renewInFlight) return renewInFlight;
  if (!isSessionValid() || !needsRenewal()) return;

  renewInFlight = (async () => {
    try {
      const response = await api.post('/auth/refresh');
      const token = response.data?.data?.token;
      if (typeof token === 'string' && token.length > 20) {
        localStorage.setItem(TOKEN_KEY, token);
      }
    } catch {
      // Offline or a transient server error — the existing token is still
      // valid, and the next tick will try again. A dead session is handled by
      // the 401 interceptor, not here.
    } finally {
      renewInFlight = null;
    }
  })();
  return renewInFlight;
};

/**
 * Start renewing the session in the background. Safe to call more than once.
 * Returns a teardown function for the caller's unmount.
 */
export const startSessionKeepAlive = (): (() => void) => {
  const tick = () => { void renewSessionIfDue(); };

  // A backgrounded tab has its timers throttled, so also check the moment the
  // vendor returns to it — that is exactly when they are about to click.
  const onVisible = () => { if (document.visibilityState === 'visible') tick(); };

  if (keepAliveTimer === null) {
    keepAliveTimer = setInterval(tick, KEEPALIVE_INTERVAL_MS);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', tick);
  }
  tick();   // catch a token that is already overdue on mount

  return () => {
    if (keepAliveTimer !== null) {
      clearInterval(keepAliveTimer);
      keepAliveTimer = null;
    }
    document.removeEventListener('visibilitychange', onVisible);
    window.removeEventListener('focus', tick);
  };
};

// Local-dev diagnostics. Browsers happily keep serving an already-loaded bundle,
// so "is the new session code actually running?" is otherwise unanswerable
// without guessing — type `__vendorSession` in the console: undefined means the
// tab is still on a stale bundle and needs a hard refresh (Ctrl+Shift+R).
// Localhost only, and read-only helpers over data the page already holds.
if (typeof window !== 'undefined' && /^(localhost|127\.0\.0\.1|\[::1\])$/.test(window.location.hostname)) {
  (window as any).__vendorSession = {
    valid: () => isSessionValid(),
    expired: () => isTokenExpired(),
    expiresAt: () => {
      const ms = getTokenExpiryMs();
      return ms === null ? 'unknown' : new Date(ms).toLocaleString();
    },
    minutesLeft: () => {
      const ms = getTokenExpiryMs();
      return ms === null ? null : Math.round((ms - Date.now()) / 60000);
    },
    dueForRenewal: () => needsRenewal(),
    renewNow: () => renewSessionIfDue()
  };
}

/** Tear down the session and send the vendor to login with an explanation. */
export const endSession = (reason: SessionEndReason): void => {
  rememberPostLoginRedirect();
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  activeDsplrId = null;   // never carry a licence scope across sessions
  try {
    sessionStorage.setItem(NOTICE_KEY, reason);
  } catch {
    /* ignore */
  }
  window.location.href = LOGIN_PATH;
};

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    // Public flows (e.g. the signup duplicate-check) can opt out of sending the
    // bearer token by setting `X-Skip-Auth`. Without this, a leftover session
    // token would make server-side "exclude self" logic skip the logged-in
    // user's own email/mobile, letting a duplicate slip past the signup form.
    if (config.headers?.['X-Skip-Auth']) {
      delete config.headers['X-Skip-Auth'];
      return config;
    }
    const token = localStorage.getItem('vendor_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // GO Ms.No.253 §6 — a Professional account holds one Displayer Licence per
    // ULB. The portal's ULB switcher selects the active licence; we transparently
    // append its dsplr_id to every scoped /display-devices request so the whole
    // page (devices, bills, notices, …) re-scopes to it. The server validates
    // ownership (resolveActiveDisplayer) and defaults to the primary when absent.
    // Excluded: the account-level licence list, and licence-application/fee
    // endpoints which carry their own ULB context.
    if (
      activeDsplrId &&
      typeof config.url === 'string' &&
      config.url.startsWith('/display-devices') &&
      !config.url.startsWith('/display-devices/licences') &&
      !config.url.startsWith('/display-devices/licence/apply') &&
      !config.url.startsWith('/display-devices/fee-preview')
    ) {
      // An explicit per-call `dsplr_id` wins over the module-level active
      // licence. The "All ULBs" view fans out one request per licence in
      // parallel; without this the shared `activeDsplrId` would stamp every
      // one of them with the same id and they'd all return the same ULB.
      config.params = {
        ...(config.params || {}),
        dsplr_id: (config.params as any)?.dsplr_id ?? activeDsplrId,
      };
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// The Displayer Licence (per-ULB) the portal is currently scoped to. Set by the
// ULB switcher on the Display Devices page; null = let the server use the
// account's primary licence. Module-scoped so every displayDevicesApi call
// picks it up via the request interceptor above without per-call plumbing.
let activeDsplrId: number | null = null;
export const setActiveDsplrId = (id: number | null): void => {
  activeDsplrId = id && Number(id) > 0 ? Number(id) : null;
};
export const getActiveDsplrId = (): number | null => activeDsplrId;

/**
 * Query params that pin ONE request to a specific Displayer Licence.
 *
 * The consolidated "All ULBs" view merges rows from every licence on the
 * account, so a row's detail / preview / PDF must be fetched against the
 * licence that OWNS it — the server's ownership checks all read
 * `req.activeDsplrId`, which would otherwise still point at whatever the
 * switcher last selected. Passing `undefined` (the single-ULB page) yields no
 * params at all, so those calls keep riding `activeDsplrId` exactly as before.
 */
const scoped = (dsplrId?: number) => (dsplrId ? { dsplr_id: dsplrId } : {});

// Response interceptor: sliding-session refresh + auth-error handling
api.interceptors.response.use(
  (response) => {
    // Sliding session — vendorAuth mints a fresh token once the current one is
    // past half its life and returns it in this header. Storing it keeps an
    // actively-working vendor signed in instead of cutting them off at the
    // fixed 24h mark, which is what made a long device-permission form vanish.
    const refreshed = response.headers?.['x-refreshed-token'];
    if (typeof refreshed === 'string' && refreshed.length > 20) {
      try {
        localStorage.setItem(TOKEN_KEY, refreshed);
      } catch {
        /* storage full / private mode — the old token is still valid for now */
      }
    }
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      const code = error.response?.data?.code as string | undefined;
      const hasToken = !!localStorage.getItem(TOKEN_KEY);
      const onLoginPage = window.location.pathname.includes('/login');

      // Only tear the session down for 401s that really are session failures.
      // A 401 raised by some other resource (a file fetch, a signing service)
      // must surface as a normal error rather than logging the vendor out.
      // Servers predating the `code` field fall back to reading the token.
      const sessionDead =
        code === 'SESSION_EXPIRED' ||
        code === 'INVALID_TOKEN' ||
        code === 'NO_TOKEN' ||
        code === 'USER_INACTIVE' ||
        (!code && (!hasToken || isTokenExpired()));

      if (hasToken && !onLoginPage && sessionDead) {
        endSession(code === 'USER_INACTIVE' ? 'inactive' : 'expired');
      }
    }
    return Promise.reject(error);
  }
);

// ========== Auth API ==========
export const authApi = {
  getCaptcha: async (): Promise<CaptchaData> => {
    const response: AxiosResponse<ApiResponse<CaptchaData>> = await api.get('/auth/captcha');
    return response.data.data;
  },

  login: async (credentials: LoginCredentials & { captcha: string; captcha_id: number }): Promise<AuthResponse> => {
    const response: AxiosResponse<ApiResponse<AuthResponse>> = await api.post('/auth/login', credentials);
    if (response.data.success) {
      localStorage.setItem('vendor_token', response.data.data.token);
      localStorage.setItem('vendor_user', JSON.stringify(response.data.data.user));
      activeDsplrId = null; // fresh account → drop any prior licence scope
    }
    return response.data.data;
  },

  // `_dev_otp` is only present when the server runs with VENDOR_OTP_DEBUG=true
  // (local dev — the MSDP SMS gateway is whitelisted to production IPs, so no
  // SMS arrives locally). In production it is absent and the OTP is SMS-only.
  //
  // `sms_sent` reports what the SMS gateway actually said: true = accepted,
  // false = rejected (nothing will arrive — show it, don't make the user wait
  // out the expiry), null = still in flight when the server replied, so it may
  // yet arrive and must NOT be reported as a failure.
  sendOtp: async (mobile: string): Promise<OtpIssueResult> => {
    // Bounded wait: the server waits on the gateway only briefly (and answers
    // `sms_sent: null` past that), so a slow response here means the API/proxy
    // is down, not a slow SMS.
    const response: AxiosResponse<ApiResponse<OtpIssueResult>> =
      await api.post('/auth/send-otp', { mobile }, { timeout: 20000 });
    return response.data.data;
  },

  verifyOtp: async (otp_ref: string, otp: string): Promise<{ verified: boolean; otp_ref: string }> => {
    const response: AxiosResponse<ApiResponse<{ verified: boolean; otp_ref: string }>> =
      await api.post('/auth/verify-otp', { otp_ref, otp });
    return response.data.data;
  },

  // Forgot password (mobile OTP) — sends an OTP to the mobile registered on an
  // existing account. The OTP is then confirmed via verifyOtp() and consumed by
  // resetPassword(). Mirrors the signup OTP flow.
  forgotPassword: async (mobile: string): Promise<OtpIssueResult> => {
    const response: AxiosResponse<ApiResponse<OtpIssueResult>> =
      await api.post('/auth/forgot-password', { mobile });
    return response.data.data;
  },

  resetPassword: async (otp_ref: string, mobile: string, new_password: string): Promise<{ success: boolean }> => {
    const response: AxiosResponse<ApiResponse<{ success: boolean }>> =
      await api.post('/auth/reset-password', { otp_ref, mobile, new_password });
    return response.data.data;
  },

  signup: async (
    payload: SignupCredentials & { otp_ref: string }
  ): Promise<{ portal_usr_id: number; eml_tx: string; dsplr_id?: number; license_status?: string }> => {
    const response: AxiosResponse<ApiResponse<{ portal_usr_id: number; eml_tx: string; dsplr_id?: number; license_status?: string }>> =
      await api.post('/auth/signup', payload);
    return response.data.data;
  },

  /**
   * Pre-flight duplicate check — server runs the same email/PAN/Aadhaar/Mobile/
   * GSTIN uniqueness queries used at signup time WITHOUT writing anything. Used by
   * the signup form to surface conflicts before the user enters the payment
   * modal so they aren't told "already exists" after paying.
   */
  checkSignupAvailability: async (payload: {
    email?: string;
    mble_ph?: string;
    pan_tx?: string;
    aadhr_tx?: string;
    gstr_tx?: string;
  }, opts?: { anonymous?: boolean }): Promise<{ available: boolean; conflict?: 'EMAIL' | 'MOBILE' | 'PAN' | 'AADHAAR' | 'GSTIN'; message?: string }> => {
    // The public signup form checks duplicates anonymously so a stale session
    // token can't self-exclude the logged-in user's own email / mobile.
    const config = opts?.anonymous ? { headers: { 'X-Skip-Auth': '1' } } : undefined;
    const response: AxiosResponse<{ success: boolean; available?: boolean; conflict?: any; message?: string }> =
      await api.post('/auth/check-availability', payload, config);
    const body = response.data || {};
    return {
      available: body.available !== false,
      conflict: body.conflict,
      message: body.message,
    };
  },

  getSignupMasters: async (): Promise<SignupMasters> => {
    const response: AxiosResponse<ApiResponse<SignupMasters>> = await api.get('/auth/signup/masters');
    return response.data.data;
  },

  logout: async (): Promise<void> => {
    try {
      await api.post('/auth/logout');
    } finally {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      activeDsplrId = null; // drop licence scope so the next account starts clean
      // A deliberate sign-out is not an expiry: clear any stale notice/return
      // path so the login page doesn't claim the session timed out.
      try {
        sessionStorage.removeItem(NOTICE_KEY);
        sessionStorage.removeItem(REDIRECT_KEY);
      } catch {
        /* ignore */
      }
    }
  },

  getProfile: async (): Promise<ContractorUser> => {
    const response: AxiosResponse<ApiResponse<ContractorUser>> = await api.get('/auth/profile');
    return response.data.data;
  },

  updateProfile: async (data: Partial<ContractorUser> & { otp_ref?: string }): Promise<ContractorUser> => {
    const response: AxiosResponse<ApiResponse<ContractorUser>> = await api.put('/auth/profile', data);
    const updatedUser = response.data.data;
    // Keep the cached user in sync with what the header/layout read, and tell
    // any already-mounted layout to re-render — ContractorLayout reads this
    // once at mount, so without the event a save is invisible until reload.
    localStorage.setItem(USER_KEY, JSON.stringify(updatedUser));
    window.dispatchEvent(new Event(USER_UPDATED_EVENT));
    return updatedUser;
  },

  // Changing the account mobile is OTP-gated (it is a login identifier and the
  // destination for every recovery OTP). Flow: sendProfileMobileOtp() → the
  // shared verifyOtp() → updateProfile({ ..., otp_ref }).
  sendProfileMobileOtp: async (mble_ph: string): Promise<{ otp_ref: string; _dev_otp?: string }> => {
    const response: AxiosResponse<ApiResponse<{ otp_ref: string; _dev_otp?: string }>> =
      await api.post('/auth/profile/mobile-otp', { mble_ph }, { timeout: 20000 });
    return response.data.data;
  },

  changePassword: async (currentPassword: string, newPassword: string): Promise<void> => {
    await api.put('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword
    });
  },

  updateTheme: async (themeId: ThemeId): Promise<{ thm_id: ThemeId }> => {
    const response: AxiosResponse<ApiResponse<{ thm_id: ThemeId }>> = await api.put('/auth/theme', {
      thm_id: themeId
    });
    return response.data.data;
  },

  getCurrentUser: (): ContractorUser | null => {
    const userStr = localStorage.getItem('vendor_user');
    return userStr ? JSON.parse(userStr) : null;
  },

  // Presence alone used to count as "logged in", so the portal kept rendering a
  // live-looking dashboard over a dead session. Check the token's own expiry.
  isAuthenticated: (): boolean => isSessionValid(),

  /** Milliseconds until the session lapses; 0 once it has. */
  timeToExpiryMs: (): number | null => {
    const expiryMs = getTokenExpiryMs();
    return expiryMs === null ? null : Math.max(0, expiryMs - Date.now());
  },

  /**
   * Replace the stored JWT in-place. Used after the apply endpoint promotes
   * the portal account from CONTRACTOR → DISPLAYER on the TEMP_APPLICANT
   * path so the next request authenticates as the new displayer.
   */
  setToken: (token: string): void => {
    if (token) localStorage.setItem(TOKEN_KEY, token);
  }
};

// ========== Dashboard API ==========
export const dashboardApi = {
  getStats: async (): Promise<DashboardStats> => {
    const response: AxiosResponse<ApiResponse<DashboardStats>> = await api.get('/dashboard/stats');
    return response.data.data;
  },

  getAlerts: async (): Promise<Alert[]> => {
    const response: AxiosResponse<ApiResponse<Alert[]>> = await api.get('/dashboard/alerts');
    return response.data.data;
  },

  getRecentActivity: async (): Promise<any[]> => {
    const response: AxiosResponse<ApiResponse<any[]>> = await api.get('/dashboard/recent-activity');
    return response.data.data;
  }
};

// ========== Works API ==========
export const worksApi = {
  getAll: async (params?: {
    status?: string;
    search?: string;
    page?: number;
    limit?: number;
    include_summary?: 'documents' | 'progress' | 'schedule' | 'financial';
  }): Promise<{
    data: ContractorWork[];
    pagination: { page: number; limit: number; total: number; totalPages: number };
  }> => {
    const response = await api.get('/works', { params });
    return { data: response.data.data, pagination: response.data.pagination };
  },

  getById: async (contractId: number): Promise<WorkDetails> => {
    const response: AxiosResponse<ApiResponse<WorkDetails>> = await api.get(`/works/${contractId}`);
    return response.data.data;
  },

  getMilestones: async (contractId: number): Promise<Milestone[]> => {
    const response: AxiosResponse<ApiResponse<Milestone[]>> = await api.get(`/works/${contractId}/milestones`);
    return response.data.data;
  },

  getTimeline: async (contractId: number): Promise<any[]> => {
    const response: AxiosResponse<ApiResponse<any[]>> = await api.get(`/works/${contractId}/timeline`);
    return response.data.data;
  },

  getParcels: async (contractId: number): Promise<any[]> => {
    const response: AxiosResponse<ApiResponse<any[]>> = await api.get(`/works/${contractId}/parcels`);
    return response.data.data;
  },

  saveSuggestionDraft: async (contractId: number, data: any): Promise<any> => {
    const response: AxiosResponse<ApiResponse<any>> = await api.post(`/works/${contractId}/parcel-suggestions`, data);
    return response.data.data;
  },

  updateSuggestionDraft: async (contractId: number, sgtnId: number, data: any): Promise<any> => {
    const response: AxiosResponse<ApiResponse<any>> = await api.put(`/works/${contractId}/parcel-suggestions/${sgtnId}`, data);
    return response.data.data;
  },

  getSuggestions: async (contractId: number): Promise<any[]> => {
    const response: AxiosResponse<ApiResponse<any[]>> = await api.get(`/works/${contractId}/parcel-suggestions`);
    return response.data.data;
  },

  deleteSuggestion: async (contractId: number, sgtnId: number): Promise<void> => {
    await api.delete(`/works/${contractId}/parcel-suggestions/${sgtnId}`);
  },

  submitSuggestions: async (contractId: number): Promise<any> => {
    const response: AxiosResponse<ApiResponse<any>> = await api.post(`/works/${contractId}/parcel-suggestions/submit`);
    return response.data.data;
  }
};

// ========== Documents API ==========
export const documentsApi = {
  getAll: async (params?: { type?: string; page?: number; limit?: number }): Promise<{
    data: ContractorDocument[];
    pagination: { page: number; limit: number; total: number; totalPages: number };
  }> => {
    const response = await api.get('/documents', { params });
    return { data: response.data.data, pagination: response.data.pagination };
  },

  getByContract: async (contractId: number, params?: { type?: string; page?: number; limit?: number }): Promise<{
    data: ContractorDocument[];
    pagination: { page: number; limit: number; total: number; totalPages: number };
  }> => {
    const response = await api.get(`/documents/${contractId}`, { params });
    return { data: response.data.data, pagination: response.data.pagination };
  },

  upload: async (
    files: File[],
    entityType: string,
    entityId: number,
    docType?: string,
    docName?: string,
    description?: string
  ): Promise<ContractorDocument[]> => {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    formData.append('entity_type_cd', entityType);
    formData.append('entity_id', entityId.toString());
    if (docType) formData.append('dcmt_type_cd', docType);
    if (docName) formData.append('dcmt_nm', docName);
    if (description) formData.append('dcmt_desc_tx', description);

    const response: AxiosResponse<ApiResponse<ContractorDocument[]>> = await api.post('/documents/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data.data;
  },

  delete: async (docId: number): Promise<void> => {
    await api.delete(`/documents/${docId}`);
  },

  getTypes: async (): Promise<DocumentType[]> => {
    const response: AxiosResponse<ApiResponse<DocumentType[]>> = await api.get('/documents/types');
    return response.data.data;
  },

  getDownloadUrl: (docId: number): string => {
    return `${BASE_URL}/documents/download/${docId}`;
  }
};

// ========== Progress API ==========
export const progressApi = {
  getHistory: async (contractId: number): Promise<ProgressUpdate[]> => {
    const response: AxiosResponse<ApiResponse<ProgressUpdate[]>> = await api.get(`/progress/${contractId}`);
    return response.data.data;
  },

  submit: async (
    contractId: number,
    data: { phscl_prgrs_nu?: number; fncl_prgrs_nu?: number; rmrks_tx?: string; lctn_lat?: number; lctn_lng?: number }
  ): Promise<ProgressUpdate> => {
    const response: AxiosResponse<ApiResponse<ProgressUpdate>> = await api.post(`/progress/${contractId}`, data);
    return response.data.data;
  },

  uploadPhotos: async (contractId: number, photos: File[]): Promise<{ photos: string[] }> => {
    const formData = new FormData();
    photos.forEach((photo) => formData.append('photos', photo));
    const response: AxiosResponse<ApiResponse<{ photos: string[] }>> = await api.post(
      `/progress/${contractId}/photos`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data.data;
  },

  getSummary: async (contractId: number): Promise<any> => {
    const response: AxiosResponse<ApiResponse<any>> = await api.get(`/progress/${contractId}/summary`);
    return response.data.data;
  }
};

// ========== Schedule API ==========
export const scheduleApi = {
  get: async (contractId: number): Promise<{ schedule: any; milestones: any[]; contract: any }> => {
    const response: AxiosResponse<ApiResponse<{ schedule: any; milestones: any[]; contract: any }>> = await api.get(
      `/schedule/${contractId}`
    );
    return response.data.data;
  },

  submitSchedule: async (contractId: number): Promise<any> => {
    const response: AxiosResponse<ApiResponse<any>> = await api.post(`/schedule/${contractId}/submit`);
    return response.data.data;
  },

  updateMilestoneProgress: async (contractId: number, milestoneId: number, data: { prgrs_pct_nu: number; rmrks_tx?: string }): Promise<any> => {
    const response: AxiosResponse<ApiResponse<any>> = await api.put(`/schedule/${contractId}/milestones/${milestoneId}/progress`, data);
    return response.data.data;
  },

  update: async (contractId: number, data: { rvsd_strt_dt?: string; rvsd_cmpltn_dt?: string; rsn_tx?: string }): Promise<any> => {
    const response: AxiosResponse<ApiResponse<any>> = await api.put(`/schedule/${contractId}`, data);
    return response.data.data;
  },

  getMilestones: async (contractId: number): Promise<Milestone[]> => {
    const response: AxiosResponse<ApiResponse<Milestone[]>> = await api.get(`/schedule/${contractId}/milestones`);
    return response.data.data;
  },

  updateMilestone: async (contractId: number, mlstnId: number, data: { rvsd_dt: string; rsn_tx?: string }): Promise<any> => {
    const response: AxiosResponse<ApiResponse<any>> = await api.put(`/schedule/${contractId}/milestones/${mlstnId}`, data);
    return response.data.data;
  },

  updateMilestoneDates: async (contractId: number, mlstnId: number, data: { plnd_strt_dt: string; plnd_end_dt: string }): Promise<any> => {
    const response: AxiosResponse<ApiResponse<any>> = await api.put(`/schedule/${contractId}/milestones/${mlstnId}/dates`, data);
    return response.data.data;
  },

  // Hindrance & EOT APIs
  getHindranceTypes: async (contractId: number): Promise<any[]> => {
    const response: AxiosResponse<ApiResponse<any[]>> = await api.get(`/schedule/${contractId}/hindrance-types`);
    return response.data.data;
  },

  getHindrances: async (contractId: number): Promise<any[]> => {
    const response: AxiosResponse<ApiResponse<any[]>> = await api.get(`/schedule/${contractId}/hindrances`);
    return response.data.data;
  },

  createHindrance: async (contractId: number, data: { hndrnc_type_id: number; dscn_tx: string; lctn_tx?: string; days_lost_nu?: number; impct_tx?: string }): Promise<any> => {
    const response: AxiosResponse<ApiResponse<any>> = await api.post(`/schedule/${contractId}/hindrances`, data);
    return response.data.data;
  },

  getEOTRequests: async (contractId: number): Promise<any[]> => {
    const response: AxiosResponse<ApiResponse<any[]>> = await api.get(`/schedule/${contractId}/eot`);
    return response.data.data;
  },

  createEOTRequest: async (contractId: number, data: { rsn_tx: string; rqstd_days_nu: number; lnkd_hndrnc_ids?: number[] }): Promise<any> => {
    const response: AxiosResponse<ApiResponse<any>> = await api.post(`/schedule/${contractId}/eot`, data);
    return response.data.data;
  }
};

// ========== Financial API ==========
export const financialApi = {
  getSummary: async (contractId: number): Promise<FinancialSummary> => {
    const response: AxiosResponse<ApiResponse<FinancialSummary>> = await api.get(`/financial/${contractId}`);
    return response.data.data;
  },

  getPayments: async (contractId: number, params?: { page?: number; limit?: number }): Promise<{
    data: Payment[];
    pagination: { page: number; limit: number; total: number; totalPages: number };
  }> => {
    const response = await api.get(`/financial/${contractId}/payments`, { params });
    return { data: response.data.data, pagination: response.data.pagination };
  },

  getBills: async (contractId: number, params?: { status?: string; page?: number; limit?: number }): Promise<{
    data: Bill[];
    pagination: { page: number; limit: number; total: number; totalPages: number };
  }> => {
    const response = await api.get(`/financial/${contractId}/bills`, { params });
    return { data: response.data.data, pagination: response.data.pagination };
  },

  getDeductions: async (contractId: number): Promise<Deduction[]> => {
    const response: AxiosResponse<ApiResponse<Deduction[]>> = await api.get(`/financial/${contractId}/deductions`);
    return response.data.data;
  },

  getRetention: async (contractId: number): Promise<any> => {
    const response: AxiosResponse<ApiResponse<any>> = await api.get(`/financial/${contractId}/retention`);
    return response.data.data;
  },

  getBankGuarantees: async (contractId: number): Promise<BankGuarantee[]> => {
    const response: AxiosResponse<ApiResponse<BankGuarantee[]>> = await api.get(`/financial/${contractId}/bank-guarantees`);
    return response.data.data;
  }
};

// ========== Users API ==========
export const usersApi = {
  getAll: async (): Promise<SubUser[]> => {
    const response: AxiosResponse<ApiResponse<SubUser[]>> = await api.get('/users');
    return response.data.data;
  },

  getById: async (userId: number): Promise<SubUser> => {
    const response: AxiosResponse<ApiResponse<SubUser>> = await api.get(`/users/${userId}`);
    return response.data.data;
  },

  create: async (data: {
    usr_nm: string;
    pwd: string;
    eml_tx?: string;
    mble_ph?: string;
    fst_nm?: string;
    lst_nm?: string;
    dsgns_tx?: string;
    prmsns_jn?: ModulePermissions;
  }): Promise<SubUser> => {
    const response: AxiosResponse<ApiResponse<SubUser>> = await api.post('/users', data);
    return response.data.data;
  },

  update: async (userId: number, data: Partial<SubUser>): Promise<SubUser> => {
    const response: AxiosResponse<ApiResponse<SubUser>> = await api.put(`/users/${userId}`, data);
    return response.data.data;
  },

  delete: async (userId: number): Promise<void> => {
    await api.delete(`/users/${userId}`);
  },

  updatePermissions: async (userId: number, permissions: ModulePermissions): Promise<SubUser> => {
    const response: AxiosResponse<ApiResponse<SubUser>> = await api.put(`/users/${userId}/permissions`, {
      prmsns_jn: permissions
    });
    return response.data.data;
  }
};

// ========== Display Devices API ==========
export interface DisplayerLicenseData {
  dsplr_id: number;
  lcnse_nu_tx: string | null;
  appl_nu_tx: string | null;
  // TRUE when this record is an enforcement stub (GO §24(e)) the account
  // adopted to pay a penalty — NOT a licence application. The page renders it
  // as an enforcement notice, not as an application in review.
  enfrcmnt_in?: boolean;
  // Whether a real licence application was ever filed on this record.
  has_application?: boolean;
  dsplr_nm: string;
  entty_nm: string | null;
  dsplr_type_nm: string | null;
  dsplr_type_cd: string | null;
  entty_type_nm: string | null;
  entty_type_cd: string | null;
  ulb_nm: string | null;
  ulb_cd: string | null;
  dstrt_nm: string | null;
  pan_tx: string | null;
  aadhr_tx: string | null;
  gstr_tx: string | null;
  mble_ph: string | null;
  ofce_ph: string | null;
  email_tx: string | null;
  rsdn_addr_tx: string | null;
  ofce_addr_tx: string | null;
  scrty_dpst_at: number | null;
  scrty_dpst_rcpt_tx: string | null;
  lcnse_fee_at: number | null;
  lcnse_fee_rcpt_tx: string | null;
  gst_at: number | null;
  /** Workflow/officer audit status — what a human last did to the licence.
   *  Never mutated by the passage of time. Render `eff_sts_cd` instead. */
  sts_cd: string | null;
  /**
   * Effective status under GO §21(c), computed in SQL by
   * `microservice.dsply_eff_lcnse_sts()`: an ACTIVE licence past its validity
   * becomes EXPIRED, and TERMINATED once more than 30 days past. This is the
   * value the UI renders and the server enforces — deriving it in the client
   * is what let the portal and the officer screens disagree.
   */
  eff_sts_cd: string | null;
  /** `lcnse_to_dt - today`, in days. Negative once expired. */
  days_to_expiry: number | null;
  /** ULB-imposed blacklist. Drives the Blacklisted badge and blocks self-cancellation. */
  blck_lstd_in: boolean | null;
  /** Annexure-I §13 self-declaration of a prior Government block-listing. Declared history only. */
  prir_blcklst_dclrd_in: boolean | null;
  lcnse_frm_dt: string | null;
  lcnse_to_dt: string | null;
  rnwl_due_dt: string | null;
  appl_dt: string | null;
  created_at: string | null;
}

/**
 * One Displayer Licence in the account's per-ULB set (GO Ms.No.253 §6),
 * as listed for the portal's ULB switcher. `is_primary` marks the account
 * anchor; `device_count` drives the "N devices" chip.
 */
/** A renewal (licence or device) parked on PENDING_PAYMENT — powers the
 *  dashboard "Pay Now" banner, identical to a fresh application. */
export interface RenewalPaymentDue {
  rnwl_id: number;
  renewal_type: string;
  is_licence: boolean;
  ref_number: string | null;
  ref_name: string | null;
  amount_due: number;
  state_cd: string;
}

/**
 * Fee owed on an Individual → Professional upgrade (GO Rule 6-I-A). The upgrade
 * is its own licence row (`dsplr_id`), so it is surfaced on the Individual
 * licence's page rather than through that licence's own workflow.
 */
export interface UpgradePaymentDue {
  dsplr_id: number;
  upgrd_id: number | null;
  lcnse_nu_tx: string | null;
  amount_due: number;
  pay_due_dt?: string | null;
  pay_wd_left?: number | null;
  pay_overdue?: boolean;
}

export interface DisplayerLicenceSummary {
  dsplr_id: number;
  ulb_id: number;
  prnt_dsplr_id: number | null;
  is_primary: boolean;
  lcnse_nu_tx: string | null;
  appl_nu_tx: string | null;
  dsplr_nm: string | null;
  sts_cd: string | null;
  /** GO §21(c) effective status — see DisplayerLicenseData.eff_sts_cd. */
  eff_sts_cd: string | null;
  days_to_expiry: number | null;
  dsplr_type_nm: string | null;
  dsplr_type_cd: string | null;
  ulb_nm: string | null;
  ulb_cd: string | null;
  dstrt_nm: string | null;
  lcnse_to_dt: string | null;
  device_count: number;
}

export interface DisplayDevicesSummary {
  license: DisplayerLicenseData | null;
  devices: { total: number; active: number; pending: number; expired: number; removed: number } | null;
  demands: {
    total_demand: number; total_collected: number; total_balance: number;
    // Fee computed on the application form, whether or not it has been billed.
    // Never part of total_balance — see getDemandSummaryMdl.
    assessed_total?: number;
    licence_balance?: number; device_balance?: number;
    licence_pending?: number; device_pending?: number;
    pending_demands: number; total_demands: number;
    signup_licence_fee?: number; signup_gst?: number; signup_deposit?: number;
  } | null;
  workflow: {
    wrkflw_inst_id: number; sts_nu: number; strt_dt: string;
    state_cd: string; state_nm: string; wrkflw_cd: string; wrkflw_nm: string;
  } | null;
  projectedRenewal?: ProjectedRenewal | null;
}

export interface DisplayDevicesDetail extends DisplayDevicesSummary {
  devices: any;
}

export interface DeviceMasters {
  types: Array<{ id: number; code: string; name: string; strl_crt_rqrd_in?: boolean; insrnce_rqrd_in?: boolean }>;
  sizes: Array<{ id: number; code: string; name: string; min_area?: number; max_area?: number }>;
  categories: Array<{ id: number; code: string; name: string; road_wdth_min?: number; road_wdth_max?: number }>;
  siteTypes: Array<{ id: number; code: string; name: string }>;
  // Empaneled structural engineers (active + valid empanelment) for the
  // Structural / Building stability certificate dropdowns.
  engineers?: Array<{
    engr_id: number; engr_nm: string; engr_reg_no?: string | null;
    engr_qlf_tx?: string | null; ltp_type_tx?: string | null;
    empnl_frm_dt?: string | null; empnl_to_dt?: string | null;
  }>;
  // GO §9(c) universities authorised to vet structural certificates. Served in
  // this vendor-authenticated bundle rather than from the ERP universities
  // route, which is staff-only and bounced vendors to the staff login page.
  universities?: Array<{
    id: number; name: string; code?: string | null;
    city?: string | null; state?: string | null; type?: string | null;
    unvrsty_id?: number; unvrsty_nm?: string; unvrsty_cd?: string | null;
  }>;
  // Per-device-type enclosure checklist from dsply_dcmt_dvce_type_rel_t, edited
  // by the ULB in the Document Types master. Replaces the wizard's hardcoded
  // document list — e.g. a floating balloon now demands the Airport Authority
  // clearance (GO §8(d)) that every type used to treat as optional.
  docRules?: Array<{
    dvce_type_id: number;
    dvce_type_cd: string;
    encl_cd: string;
    dcmt_type_cd: string;
    name: string;
    hint: string | null;
    mandatory: boolean;
  }>;
}

/** One ward secretariat: centroid always, polygon when the boundary is imported. */
export interface MapSachivalayam {
  svm_id: number;
  svm_nm: string;
  svm_cd: string | null;
  lat: number | null;
  lng: number | null;
  /** GeoJSON geometry (Polygon / MultiPolygon / LineString), or null. */
  geometry: any | null;
}

/** An already-standing device, anonymised — no displayer/owner identity. */
export interface MapDevice {
  dvce_id: number;
  unque_id_tx: string | null;
  lat: number;
  lng: number;
  lctn_tx: string | null;
  ward_tx: string | null;
  ward_no: number | null;
  dvce_type_nm: string | null;
  area_sqft_nu: number | null;
  sts_cd: string | null;
  prmnt_in: boolean | null;
  is_hoarding: boolean;
}

/** A notified prohibited place with its GO Rule 12 radius. */
export interface MapProhibitedPlace {
  id: number;
  name: string;
  code: string | null;
  lat: number;
  lng: number;
  radius_mtrs: number;
  go_ref: string;
}

export interface DeviceMapContext {
  ulb_id: number;
  ulb_centre: { lat: number; lng: number } | null;
  /** GeoJSON Polygon of the ULB's outer boundary, or null when not imported. */
  ulb_boundary: any | null;
  sachivalayams: MapSachivalayam[];
  devices: MapDevice[];
  prohibited: MapProhibitedPlace[];
  rules: {
    /** GO Ms.No.253 Rule 15(b) — 50 m between two hoardings on the same side. */
    min_hoarding_gap_mtrs: number;
    min_hoarding_gap_ref: string;
  };
}

export interface DeviceApplicationRow {
  dvce_id: number;
  unque_id_tx: string | null;
  appl_nu_tx: string | null;
  sts_cd: string | null;
  dvce_type_nm: string | null;
  lctn_tx: string | null;
  prmsn_frm_dt: string | null;
  prmsn_to_dt: string | null;
  ttl_fee_at: number | null;
  i_ts: string | null;
}

/** One display-content entry logged against a device (GO Ms.No.253 Rule 18). */
export interface ContentHistoryRow {
  cntnt_id: number;
  dvce_id: number;
  dsplr_id: number | null;
  cntnt_titl_tx: string;
  cntnt_dscn_tx: string | null;
  advrtsr_nm: string | null;
  cntnt_ctgry_cd: string | null;
  cntnt_lang_cd: string | null;
  dsply_frm_dt: string | null;
  dsply_to_dt: string | null;
  cntnt_photo_path_tx: string | null;
  cntnt_photo_nm_tx: string | null;
  cntnt_photo2_path_tx: string | null;
  cntnt_photo2_nm_tx: string | null;
  cntnt_video_path_tx: string | null;
  cntnt_video_nm_tx: string | null;
  rule18_cnsnt_in: boolean;
  sts_cd: string | null;
  flag_rsn_tx: string | null;
  flag_clause_tx: string | null;
  actn_taken_cd: string | null;
  rmrks_tx: string | null;
  created_at: string | null;
}

/** Payload for creating a display-content entry. */
export interface CreateContentPayload {
  cntnt_titl_tx: string;
  cntnt_dscn_tx?: string | null;
  advrtsr_nm?: string | null;
  cntnt_ctgry_cd?: string | null;
  cntnt_lang_cd?: string | null;
  dsply_frm_dt: string;
  dsply_to_dt: string;
  cntnt_photo_path_tx?: string | null;
  cntnt_photo_nm_tx?: string | null;
  cntnt_photo2_path_tx?: string | null;
  cntnt_photo2_nm_tx?: string | null;
  cntnt_video_path_tx?: string | null;
  cntnt_video_nm_tx?: string | null;
  rule18_cnsnt_in: boolean;
  cnsnt_txt_tx?: string | null;
  cnsnt_vrsn_tx?: string | null;
}

/**
 * Turn an axios error from a `responseType: 'blob'` request into one that
 * carries the server's message instead of an opaque Blob. Callers `throw` the
 * result.
 *
 * A PDF endpoint that fails answers with a JSON (or plain-text) body, but the
 * blob response type means axios hands it back as a Blob, so `err.message` is
 * just "Request failed with status code 404". This reads the body and rewraps
 * the error around the server's own sentence.
 *
 * The JSON parse is confined to its own try/catch and the wrapped error is
 * built OUTSIDE it. An earlier version threw the wrapped error from inside the
 * try, where its own bare `catch` swallowed it and fell through to the raw-body
 * branch — which is why the receipt preview showed the literal text
 * `{"success":false,"message":"…"}` rather than the message itself.
 */
async function asBlobError(err: any): Promise<any> {
  const blob = err?.response?.data;
  if (!(blob instanceof Blob)) return err;
  const text = await blob.text().catch(() => '');
  let message = '';
  try {
    const parsed = JSON.parse(text);
    message = String(parsed?.message || parsed?.error || '');
  } catch { /* not JSON — fall back to the raw body */ }
  if (!message && text) message = text.slice(0, 500);
  if (!message) return err;
  const wrapped: any = new Error(message);
  wrapped.response = { ...(err.response || {}), data: { message } };
  return wrapped;
}

/**
 * Inspections, device-wise. Each row is one of the vendor's devices with where
 * it stands against its GO Rule 17(iii) schedule (`schedule_state`), when it is
 * next due, and what the latest inspection found.
 */
export interface InspectionDeviceRow {
  dvce_id: number;
  unque_id_tx: string;
  lctn_tx?: string | null;
  dvce_type_nm?: string | null;
  area_sqft_nu?: string | number | null;
  schedule_state: 'OVERDUE' | 'DUE' | 'ON_SCHEDULE';
  next_due_dt_fmt?: string | null;
  next_due_strm_cd?: string | null;
  days_to_due?: number | null;
  never_inspected: boolean;
  last_inspcn_id?: number | null;
  last_inspcn_dt_fmt?: string | null;
  last_inspcn_type_cd?: string | null;
  last_cmplnce_in?: boolean | null;
  last_inspctr_nm?: string | null;
  non_compliant: boolean;
  inspection_count: number;
  /** Stamped by the All-ULBs view so per-row calls hit the right licence. */
  __dsplr_id?: number;
  __ulb_nm?: string;
}

export interface InspectionDevicesResult {
  devices: InspectionDeviceRow[];
  counts: { ACTION_REQUIRED: number; DUE_SOON: number; ALL: number };
  /** FALSE on a deployment where the schedule register is not set up yet. */
  schedule_available: boolean;
}

export interface DeviceInspectionHistory {
  device: any | null;
  /** The cycles this device still owes — a unipole carries two. */
  cycles: Array<{
    strm_cd: string; strm_nm?: string | null; go_rule_tx?: string | null;
    cycle_nu: number; cadence_dys_nu: number; due_dt_fmt: string; days_to_due: number;
  }>;
  inspections: Array<{
    inspcn_id: number; inspcn_dt_fmt: string;
    inspcn_type_cd?: string | null; inspcn_type_nm?: string | null;
    inspctr_nm?: string | null; inspctr_dsgntn_tx?: string | null;
    cmplnce_in?: boolean | null; strl_cndtn_cd?: string | null;
    actn_rqrd_tx?: string | null; is_latest: boolean;
  }>;
}

export const displayDevicesApi = {
  getSummary: async (): Promise<DisplayDevicesSummary> => {
    const response = await api.get('/display-devices/summary');
    return response.data.data;
  },
  /**
   * Full licence detail. `dsplrId` targets ONE specific licence regardless of
   * the module-level active selection — the "All ULBs" view uses it to fan out
   * across every licence on the account in parallel. Omit it for the normal
   * single-ULB page, which follows the ULB switcher.
   */
  getDetail: async (dsplrId?: number): Promise<{ license: DisplayerLicenseData | null; devices: any[]; demands: any; workflow: any; licences?: DisplayerLicenceSummary[]; renewalPayments?: RenewalPaymentDue[]; upgradePayment?: UpgradePaymentDue | null }> => {
    const response = await api.get('/display-devices/detail', dsplrId ? { params: { dsplr_id: dsplrId } } : undefined);
    return response.data.data;
  },
  /** All Displayer Licences on the account (per-ULB) — feeds the ULB switcher. */
  getLicences: async (): Promise<DisplayerLicenceSummary[]> => {
    const response = await api.get('/display-devices/licences');
    return response.data.data || [];
  },
  /** Fee preview — calculates licence fee + security deposit based on ULB from DB rate tables. */
  getFeePreview: async (ulbId: number, ulbNm?: string, ulbGrade?: string): Promise<any> => {
    const response = await api.get('/display-devices/fee-preview', {
      params: { ulb_id: ulbId, ulb_nm: ulbNm || '', ulb_grade: ulbGrade || '' }
    });
    return response.data.data;
  },
  /**
   * Advisory Property Tax check of an assessment number against the licence
   * ULB's PT records. FOUND carries owner / door no. / ward; NOT_FOUND is a
   * warning only (the premises may be in another ULB); UNAVAILABLE means the
   * ULB has no PT data to check against.
   */
  verifyPtAssessment: async (ulbId: number | string, assessmentNo: string): Promise<PtAssessmentCheck> => {
    const response = await api.get('/display-devices/pt-assessment/verify', {
      params: { ulb_id: ulbId, assessment_no: assessmentNo },
    });
    return response.data.data;
  },
  /**
   * Device-permission RATE for one device type, from the admin Setup tariff
   * (dsply_prmsn_rate_lst_t — no hard-coded rates). Returns the per-sq.m /
   * per-unit rate (escalated to the current FY) + illumination surcharge %.
   * `rate` is null when no slab is configured. The caller multiplies by
   * area/units and layers GO-fixed modifiers (PPP / encroachment / lease / GST)
   * on top.
   *
   * Also returns the slab's GO Rule 13-d weekly-proration settings. Pass `days`
   * (inclusive event duration) to have the server resolve the duration factor
   * with the same function the officer-side demand uses; omit it and the factor
   * comes back as 1 (flat full-period rate).
   */
  getDeviceFeePreview: async (params: {
    dvce_type_id: number | string;
    prmnt_in: boolean;
    ulb_id?: number | string | null;
    ulb_nm?: string | null;
    ulb_grade?: string | null;
    days?: number | null;
  }): Promise<{
    rate: number | null; rate_unit_cd: 'PER_SQM' | 'PER_UNIT'; illumination_pct: number;
    gst_pct: number; configured: boolean;
    weekly_proration?: boolean; rate_period_days?: number; weekly_fraction?: number;
    duration_factor?: number; weeks?: number | null; proration_applies?: boolean;
  }> => {
    const response = await api.get('/display-devices/device-fee-preview', {
      params: {
        dvce_type_id: params.dvce_type_id,
        prmnt_in: params.prmnt_in,
        ulb_id: params.ulb_id ?? '',
        ulb_nm: params.ulb_nm ?? '',
        ulb_grade: params.ulb_grade ?? '',
        ...(params.days ? { days: params.days } : {}),
      },
    });
    return response.data.data;
  },
  /** Master data for the Apply-Device wizard (types / sizes / categories / site types). */
  getMasters: async (): Promise<DeviceMasters> => {
    const response = await api.get('/display-devices/masters');
    return response.data.data;
  },
  /**
   * DTCP location for the permission wizard: which UDA, and which village
   * sachivalayam inside it. Replaces the ULB + ward-secretariat pair the wizard
   * inherited from PuraMitra — a display device out here stands on rural land,
   * where a ward is not a smaller ULB but a different object entirely.
   *
   * Shaped as the LocationCascade component's `source` contract, which takes the
   * raw envelope (`{ data: rows }`) rather than the unwrapped rows every other
   * call here returns. That is deliberate: the officer form and this wizard then
   * drive the SAME component through the same interface, and the only difference
   * between them is which door the request goes through — these endpoints are
   * the vendor-auth mount of the officer controller, which a vendor token
   * cannot reach directly.
   *
   * Only the two levels the compact picker uses exist. Constituency, mandal and
   * panchayat are not fetched because nothing stores them: they narrow a
   * dropdown the compact picker does not render.
   *
   * The UDA is NOT sent. The account already answered that question at signup,
   * and the server reads it off the licence — so the wizard asks for one field,
   * not two. `udas` below is only reached when that derivation fails.
   */
  location: {
    getUdas: async () => {
      const response = await api.get('/display-devices/masters/location/udas');
      return response.data;
    },
    /**
     * Villages for this account's own UDA. Answers with the derived `uda_id`
     * and this displayer's `last_schvlm_id` alongside the rows, so the picker
     * can record where the application will be filed and offer the previous
     * choice without a second round trip.
     *
     * `ulbId` is only for an applicant with no licence yet (Annexure-V), where
     * the wizard's own ULB picker is the only thing that knows the authority. A
     * licensed account's ULB comes off the licence server-side and this is
     * ignored. `udaId` is honoured only on the fallback path, after the server
     * failed to derive one and the wizard had to ask.
     */
    getVillageSachivalayams: async (
      params: { pnchytId?: number; mndlId?: number; udaId?: number; ulbId?: number } = {},
    ) => {
      const qp: Record<string, number> = {};
      if (params.ulbId != null) qp.ulb_id = params.ulbId;
      if (params.udaId != null) qp.uda_id = params.udaId;
      const response = await api.get('/display-devices/masters/location/sachivalayams', { params: qp });
      return response.data;
    },
    /**
     * The outline to frame the site picker on for one village.
     *
     * Often NOT the village: no secretariat boundary layer has been loaded, so
     * what usually comes back is the mandal. `source_level` says which level
     * answered and the map has to caption it — drawing a mandal and calling it
     * the village would tell an applicant their pin is inside a village it may
     * be nowhere near. `centroid_level` is reported separately because the
     * centre CAN be the village's own point while the outline is not.
     */
    getVillageExtent: async (schvlmId: number) => {
      const response = await api.get('/display-devices/masters/location/village-extent', {
        params: { schvlm_id: schvlmId },
      });
      return response.data?.data ?? null;
    },
  },
  /**
   * Everything the site-picker map needs for one ULB, in a single call:
   * ward-secretariat polygons + centroids, the device pins already standing in
   * that ULB (anonymised), the notified prohibited places (GO Rule 12) with
   * their radius, and the GO separation rule the dropped pin is checked
   * against (Rule 15(b), 50 m between hoardings).
   *
   * Vendor-side endpoint on purpose — the officer dashboard's GIS/ERP clients
   * are staff-auth and 401 out of the vendor portal.
   */
  getMapContext: async (ulbId: number | string): Promise<DeviceMapContext> => {
    const response = await api.get('/display-devices/map/context', { params: { ulb_id: ulbId } });
    return response.data.data;
  },
  /** Submit a new device-permission application (creates device row + workflow instance + DCB receipt). */
  apply: async (payload: Record<string, any>): Promise<{ dvce_id: number; wrkflw_inst_id?: number; rcpt_id?: number | null; rcpt_nu_tx?: string | null; dmnd_id?: number | null; token?: string | null }> => {
    const response = await api.post('/display-devices/apply', payload);
    return response.data.data;
  },
  /**
   * Apply to REGULARIZE an already-erected unauthorised device
   * (GO Ms.No.253 §14/§24). Same device payload as `apply`; the server flags
   * it as a regularization, routes it through the DEVICE_PERMISSION officer
   * workflow, and adds a 200% penalty to the fee at the pay-after-TPO gate.
   * Returns the fee breakdown (nrml_fee_at / pnlty_fee_at / rglrztn_ttl_fee_at).
   */
  applyRegularization: async (payload: Record<string, any>): Promise<{ dvce_id: number; wrkflw_inst_id?: number; rglrztn_id?: number | null; nrml_fee_at?: number | null; pnlty_fee_at?: number | null; rglrztn_ttl_fee_at?: number | null; token?: string | null }> => {
    const response = await api.post('/display-devices/apply-regularization', payload);
    return response.data.data;
  },
  /** The logged-in displayer's own regularization applications + live workflow state. */
  getRegularizations: async (): Promise<any[]> => {
    const response = await api.get('/display-devices/regularizations');
    return response.data.data || [];
  },
  /**
   * Submit multiple device-permission applications in a single payment.
   * `devices` is the array of per-device payloads (same shape as `apply`,
   * minus the receipt fields), `payment` carries the shared receipt that
   * gets stamped onto every row. Each device gets its own DCB receipt
   * (rcpt_id / rcpt_nu_tx) so vendors can download a per-device receipt.
   *
   * `applicant` is sent only on the unlicensed Temporary Display Device path
   * (GO Ms.No.253 Annexure-V): the server materialises a TEMP_APPLICANT
   * displayer row from it and returns a refreshed JWT in `token`.
   */
  applyBulk: async (
    devices: Array<Record<string, any>>,
    payment?: Partial<{ dev_fee_at: number; dev_gst_at: number; dev_fee_rcpt_tx: string; dev_fee_pd_dt: string; dev_fee_mthd_cd: string; total_at: number }>,
    applicant?: Record<string, any> | null,
  ): Promise<{ count: number; receipt: string; devices: Array<{ dvce_id: number; unque_id_tx: string | null; wrkflw_inst_id?: number | null; rcpt_id?: number | null; rcpt_nu_tx?: string | null; dmnd_id?: number | null }>; token?: string | null }> => {
    const body: any = { devices, payment };
    if (applicant) body.applicant = applicant;
    const response = await api.post('/display-devices/apply-bulk', body);
    return response.data.data;
  },
  /** Vendor's own device-permission applications, optionally filtered by status. */
  getApplications: async (status?: string): Promise<DeviceApplicationRow[]> => {
    const response = await api.get('/display-devices/applications', { params: { sts_cd: status || '' } });
    return response.data.data || [];
  },
  /** Notices issued to the logged-in displayer (drafts hidden by the server).
   *  Pass `dsplrId` to target one licence regardless of the active selection. */
  getNotices: async (dsplrId?: number): Promise<any[]> => {
    const response = await api.get('/display-devices/notices', dsplrId ? { params: { dsplr_id: dsplrId } } : undefined);
    return response.data.data || [];
  },

  /**
   * Rendered notice HTML — same Government letterhead preview the admin
   * Notices page uses. Server delegates to the admin's notice template.
   */
  getNoticeHtml: async (noticeId: number, dsplrId?: number): Promise<{ html: string; format: string }> => {
    const response = await api.get(`/display-devices/notices/${noticeId}/pdf`, { params: scoped(dsplrId) });
    return response.data.data;
  },
  /** Download the same notice as PDF (triggers browser save-as). */
  downloadNoticePdf: async (noticeId: number, suggestedName?: string, dsplrId?: number): Promise<void> => {
    const response = await api.get(`/display-devices/notices/${noticeId}/pdf`, {
      params: { format: 'pdf', ...scoped(dsplrId) }, responseType: 'blob',
    });
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const href = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = href;
    a.download = suggestedName || `Notice-${noticeId}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(href);
  },
  /**
   * Fetch one of a notice's supporting attachments as an object URL. Goes
   * through the vendor-scoped, ownership-checked endpoint (not the raw ERP
   * filestore route, which the vendor token can't authenticate). Returns the
   * object URL + resolved content type; caller must revokeObjectURL when done.
   */
  getNoticeAttachmentObjectUrl: async (
    noticeId: number,
    idx: number,
    dsplrId?: number,
  ): Promise<{ url: string; contentType: string }> => {
    const response = await api.get(
      `/display-devices/notices/${noticeId}/attachments/${idx}`,
      { params: scoped(dsplrId), responseType: 'blob' },
    );
    const contentType = response.data?.type || response.headers?.['content-type'] || '';
    const url = window.URL.createObjectURL(response.data);
    return { url, contentType };
  },
  /** Penalties issued by the admin to the vendor's devices/displayer (Enforcement).
   *  Pass `dsplrId` to target one licence regardless of the active selection. */
  getPenalties: async (dsplrId?: number): Promise<any[]> => {
    const response = await api.get('/display-devices/penalties', dsplrId ? { params: { dsplr_id: dsplrId } } : undefined);
    return response.data.data || [];
  },
  /**
   * Unauthorised-device penalties standing against this account's MOBILE
   * NUMBER (GO Ms.No.253 §24(e)) — raised by a field officer before the person
   * ever registered, so they are not attached to a licence the normal way.
   *
   * `blocking` gates new licence / device-permission applications until paid.
   * `info` is a device on this person's property that someone else erected —
   * shown for awareness only, never blocking.
   *
   * Resolves to empty on error: a failed lookup must not make the page look
   * broken, and the server-side gate is the real enforcement point anyway.
   */
  getEnforcementDues: async (): Promise<{
    blocking: any[]; info: any[]; blocking_total: number; info_total: number;
  }> => {
    try {
      const response = await api.get('/display-devices/enforcement-dues');
      return response.data.data || { blocking: [], info: [], blocking_total: 0, info_total: 0 };
    } catch {
      return { blocking: [], info: [], blocking_total: 0, info_total: 0 };
    }
  },
  /** Rendered penalty HTML — same GO §24 letterhead the admin uses. */
  getPenaltyHtml: async (penaltyId: number, dsplrId?: number): Promise<{ html: string; format: string }> => {
    const response = await api.get(`/display-devices/penalties/${penaltyId}/pdf`, { params: scoped(dsplrId) });
    return response.data.data;
  },
  /** Download the penalty document as PDF (triggers browser save-as). */
  downloadPenaltyPdf: async (penaltyId: number, suggestedName?: string, dsplrId?: number): Promise<void> => {
    const response = await api.get(`/display-devices/penalties/${penaltyId}/pdf`, {
      params: { format: 'pdf', ...scoped(dsplrId) }, responseType: 'blob',
    });
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const href = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = href;
    a.download = suggestedName || `Penalty-${penaltyId}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(href);
  },
  /** Simulated penalty payment — flips penalty + linked demand to PAID. */
  payPenalty: async (penaltyId: number | string): Promise<{ penalty_id: number; receipt_no: string; paid_amount: number; message: string }> => {
    const response = await api.post(`/display-devices/penalties/${penaltyId}/pay`);
    return response.data;
  },
  /**
   * Begin an online CFMS payment for an enforcement penalty. Returns
   * { gatewayUrl } to redirect the browser to (or { already_processed }). Only
   * works when the server has DS_CFMS_ENABLED=true — otherwise 409, and the
   * caller should fall back to payPenalty.
   */
  payPenaltyInitiate: async (penaltyId: number | string): Promise<any> => {
    const response = await api.post(`/display-devices/penalties/${penaltyId}/pay/initiate`);
    return response.data;
  },
  /**
   * The vendor's devices with their inspection position: when each is next due
   * under GO Ms.No.253 Rule 17(iii), and what its latest inspection found.
   *
   * One row per DEVICE, not per inspection record — the same model the
   * corporation's own Inspections register reads. Pass `dsplrId` to target one
   * licence regardless of the active selection.
   */
  getInspections: async (dsplrId?: number): Promise<InspectionDevicesResult> => {
    const response = await api.get('/display-devices/inspections', dsplrId ? { params: { dsplr_id: dsplrId } } : undefined);
    const d = response.data?.data || {};
    return {
      devices: Array.isArray(d.devices) ? d.devices : [],
      counts: d.counts || { ACTION_REQUIRED: 0, DUE_SOON: 0, ALL: 0 },
      schedule_available: d.schedule_available !== false,
    };
  },
  /**
   * One device's inspections, newest first, with the cycles it still owes.
   * Inspections the corporation marked invalid are not returned at all.
   */
  getDeviceInspections: async (dvceId: number | string, dsplrId?: number): Promise<DeviceInspectionHistory> => {
    const response = await api.get(`/display-devices/devices/${dvceId}/inspections`, { params: scoped(dsplrId) });
    const d = response.data?.data || {};
    return {
      device: d.device || null,
      cycles: Array.isArray(d.cycles) ? d.cycles : [],
      inspections: Array.isArray(d.inspections) ? d.inspections : [],
    };
  },
  /** Rendered inspection-report HTML — same GO §17 letterhead the ERP uses. */
  getInspectionHtml: async (inspcnId: number, dsplrId?: number): Promise<{ html: string; format: string }> => {
    const response = await api.get(`/display-devices/inspections/${inspcnId}/pdf`, { params: scoped(dsplrId) });
    return response.data.data;
  },
  /** Download the inspection report as PDF (triggers browser save-as). */
  downloadInspectionPdf: async (inspcnId: number, suggestedName?: string, dsplrId?: number): Promise<void> => {
    const response = await api.get(`/display-devices/inspections/${inspcnId}/pdf`, {
      params: { format: 'pdf', ...scoped(dsplrId) }, responseType: 'blob',
    });
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const href = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = href;
    a.download = suggestedName || `Inspection-${inspcnId}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(href);
  },
  /** Structural-stability certificates uploaded by the admin for one device. */
  getDeviceStructuralCerts: async (dvceId: number | string, dsplrId?: number): Promise<any[]> => {
    const response = await api.get(`/display-devices/devices/${dvceId}/structural-certificates`, { params: scoped(dsplrId) });
    return response.data.data || [];
  },
  /**
   * Download the tax-invoice PDF for a Bills & Payments row.
   * - `type`: 'licence-fee' | 'security-deposit' | 'device-fee'
   * - `dvce_id`: required only for 'device-fee'
   * Streams the PDF and triggers a browser download.
   */
  downloadInvoice: async (
    type: 'licence-fee' | 'security-deposit' | 'device-fee',
    opts: { dvce_id?: number; filename?: string } = {},
  ): Promise<void> => {
    try {
      const response = await api.get('/display-devices/invoice', {
        params: { type, format: 'pdf', dvce_id: opts.dvce_id },
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = opts.filename || `Invoice-${type}${opts.dvce_id ? `-${opts.dvce_id}` : ''}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      const blob = err?.response?.data;
      if (blob instanceof Blob) {
        const text = await blob.text().catch(() => '');
        try {
          const parsed = JSON.parse(text);
          if (parsed?.message) {
            const wrapped: any = new Error(parsed.message);
            wrapped.response = { ...(err.response || {}), data: parsed };
            throw wrapped;
          }
        } catch { /* fallthrough */ }
        if (text) {
          const wrapped: any = new Error(text.slice(0, 500));
          wrapped.response = { ...(err.response || {}), data: { message: text.slice(0, 500) } };
          throw wrapped;
        }
      }
      throw err;
    }
  },
  /**
   * Submit a vendor-initiated cancellation request for an ACTIVE device
   * permission. GO basis is Rule 21(c) Note (b) — the displayer informs the
   * ULB in writing that they will not extend the device permission, and a
   * late-notice penalty of 1/4 of the permission fee applies if that notice
   * comes within 30 days of expiry. NOT Rule 27, which is the ULB's own
   * withdrawal power. The device flips to CANCEL_REQUESTED and lands in the
   * officer queue for review.
   */
  requestDeviceCancellation: async (
    dvceId: number,
    payload: {
      grnd_cd: 'NO_LONGER_REQUIRED' | 'SITE_VACATED' | 'STRUCTURAL_CONCERNS' | 'SWITCHING_DEVICE_TYPE' | 'OTHER';
      grnd_desc_tx?: string;
      cncl_rsn_tx?: string;
    },
  ): Promise<any> => {
    const r = await api.post(`/display-devices/devices/${dvceId}/cancel-request`, payload);
    return r.data;
  },

  /** Pull back a still-pending cancellation request — returns the device to ACTIVE. */
  withdrawDeviceCancellation: async (dvceId: number): Promise<any> => {
    const r = await api.post(`/display-devices/devices/${dvceId}/cancel-request/withdraw`);
    return r.data;
  },

  /**
   * Submit a device-permission renewal application for a device the vendor owns.
   * GO §16(b): server enforces the 30-day pre-expiry window and rejects when an
   * open renewal already exists for the device. Server returns 201 with the
   * renewal id and computed totals on success.
   */
  requestDeviceRenewal: async (
    dvceId: number,
    payload: {
      gst_nil_verified: boolean;
      prior_period_compliance: boolean;
      remarks?: string;
      // GO Ms.No.253 Rule 15(e) — the concession is re-claimed per renewal,
      // never carried forward from the expiring permission. Omitting these bills
      // the full Annexure-VIII tariff; sending ppp_in requires BOTH the council
      // resolution number and a fresh PPP_PROOF enclosure, or the server rejects.
      ppp_in?: boolean;
      ppp_cncl_aprvl_tx?: string;
      enclosures?: Array<{ dcmt_type_cd: string; dcmt_path_tx: string; file_nm_tx?: string }>;
    },
  ): Promise<any> => {
    const r = await api.post(`/display-devices/devices/${dvceId}/renew`, payload);
    return r.data;
  },

  /**
   * Simulate payment of a renewal — server marks renewal APPROVED, advances
   * the device's `prmsn_to_dt` by 3 years and returns a stable mock receipt
   * number. Idempotent — calling it twice on the same renewal returns the
   * existing receipt instead of creating a duplicate.
   */
  payDeviceRenewalSimulated: async (rnwlId: number): Promise<any> => {
    const r = await api.post(`/display-devices/renewals/${rnwlId}/pay-simulated`);
    return r.data;
  },

  /**
   * Begin an online CFMS payment for a device-permission renewal. Returns
   * { gatewayUrl } to redirect the browser to (or { already_processed }). Only
   * works when DS_CFMS_ENABLED=true — otherwise 409, and the caller should fall
   * back to payDeviceRenewalSimulated.
   */
  payDeviceRenewalInitiate: async (rnwlId: number): Promise<any> => {
    const r = await api.post(`/display-devices/renewals/${rnwlId}/pay/initiate`);
    return r.data;
  },

  /**
   * Resubmit a renewal the officer sent back (RETURNED_TO_APPLICANT). Renewals
   * carry no documents, so this just re-fires RESUBMIT (→ WPRS) with an optional
   * applicant note.
   */
  resubmitRenewal: async (rnwlId: number, remarks?: string): Promise<any> => {
    const r = await api.post(`/display-devices/renewals/${rnwlId}/resubmit`, { remarks: remarks || null });
    return r.data;
  },

  /**
   * Pay-after-TPO fee settlement for a fresh application (GO Ms.No.253).
   * Called once an application or renewal is parked on PENDING_PAYMENT (the Town
   * Planning Officer has verified & forwarded it). Settles the PENDING fee demand
   * and advances the workflow — DISPLAYER + licence RENEWAL → Commissioner for
   * final approval; DEVICE + device RENEWAL → auto-issued. For a RENEWAL, refId
   * is the renewal id.
   */
  payApplication: async (
    refTypeCd: 'DEVICE' | 'DISPLAYER' | 'RENEWAL',
    refId: number,
    payment?: { mthdCd?: string; instrumentNo?: string; instrumentDt?: string; bankNm?: string; bankBrnch?: string },
  ): Promise<any> => {
    const r = await api.post(`/display-devices/pay-application`, {
      ref_type_cd: refTypeCd,
      ref_id: refId,
      payment: payment || { mthdCd: 'CASH' },
    });
    return r.data;
  },

  /**
   * Begin an online CFMS payment for a pay-after-TPO fee. Returns
   * { gatewayUrl } to redirect the browser to (or { already_processed } if the
   * fee was already paid). Only works when the server has DS_CFMS_ENABLED=true —
   * otherwise it 409s and the caller should fall back to payApplication.
   */
  payApplicationInitiate: async (
    refTypeCd: 'DEVICE' | 'DISPLAYER' | 'RENEWAL',
    refId: number,
  ): Promise<any> => {
    const r = await api.post(`/display-devices/pay-application/initiate`, {
      ref_type_cd: refTypeCd,
      ref_id: refId,
    });
    return r.data;
  },

  /**
   * Whether the online CFMS payment flow is enabled server-side, and (local dev
   * only) whether a no-money simulated settle is allowed as a fallback.
   * `simulatedAllowed` is true only when CFMS is off AND the server opted in with
   * DS_ALLOW_SIMULATED_PAYMENT=true — it is always false on UAT/prod.
   */
  getPaymentConfig: async (): Promise<{ success: boolean; cfmsEnabled: boolean; simulatedAllowed?: boolean }> => {
    const r = await api.get(`/display-devices/payment-config`);
    return r.data;
  },

  /**
   * Ask the server to check an open CFMS attempt with CFMS TransStatus and settle
   * it if CFMS confirms the money — for when the bank redirect came back pending
   * or never came back. Ownership-scoped server-side.
   */
  verifyPayment: async (body: { kind?: string; key_id?: number; ds_pay_id?: number; ref_type_cd?: string; ref_id?: number }): Promise<{
    success: boolean; status?: 'success' | 'failed' | 'pending'; message?: string;
    already_processed?: boolean; receipt?: string; receipt_id?: string; amount?: string;
  }> => {
    const r = await api.post(`/display-devices/payments/verify`, body);
    return r.data;
  },

  /**
   * Itemised breakdown of what the amount payable is FOR (Licence Fee /
   * Security Deposit / GST / …) — powers the Pay Now modal's line-item list so
   * the vendor sees which charges make up the total. `data` is null when no
   * demand exists yet, in which case the modal falls back to the plain total.
   */
  getPayableBreakdown: async (
    refTypeCd: 'DEVICE' | 'DISPLAYER' | 'RENEWAL',
    refId: number,
  ): Promise<{
    dmnd_id: number;
    dmnd_nu_tx: string | null;
    total: number;
    balance: number;
    lines: Array<{ code: string; label: string; amount: number }>;
  } | null> => {
    const r = await api.get(`/display-devices/payable-breakdown`, {
      params: { refType: refTypeCd, refId },
    });
    return r.data?.data ?? null;
  },

  /**
   * Stream a per-device Annexure PDF for the logged-in vendor's displayer
   * and trigger a browser download.
   *
   * `kind` selects which annexure (the server picks III/V or IV/VI based on
   * whether the device is permanent or temporary):
   *   - 'application' → Annexure-III (permanent) or Annexure-V (temporary)
   *   - 'order'       → Annexure-IV (permanent) or Annexure-VI (temporary)
   *
   * The server returns 400 if 'order' is requested for a non-ACTIVE device.
   */
  downloadDeviceAnnexure: async (
    dvceId: number,
    kind: 'application' | 'order',
    filename?: string,
    type?: 'FRESH' | 'RENEWAL',
    /** Pin a PAST term's archived order instead of the current one. */
    termId?: number,
    dsplrId?: number,
  ): Promise<void> => {
    try {
      const response = await api.get(`/display-devices/devices/${dvceId}/annexure/${kind}`, {
        params: { format: 'pdf', ...(type ? { type } : {}), ...(termId ? { term: termId } : {}), ...scoped(dsplrId) },
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || `Annexure-${kind === 'application' ? 'Application' : 'Order'}-${dvceId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      const blob = err?.response?.data;
      if (blob instanceof Blob) {
        const text = await blob.text().catch(() => '');
        try {
          const parsed = JSON.parse(text);
          if (parsed?.message) {
            const wrapped: any = new Error(parsed.message);
            wrapped.response = { ...(err.response || {}), data: parsed };
            throw wrapped;
          }
        } catch { /* fallthrough */ }
        if (text) {
          const wrapped: any = new Error(text.slice(0, 500));
          wrapped.response = { ...(err.response || {}), data: { message: text.slice(0, 500) } };
          throw wrapped;
        }
      }
      throw err;
    }
  },
  /**
   * Stream the Annexure-II licence proceedings PDF for the logged-in
   * displayer and trigger a browser download. Server gates on ACTIVE
   * licences; callers should disable the trigger otherwise.
   */
  downloadLicencePdf: async (filename = 'Displayer-Licence.pdf'): Promise<void> => {
    try {
      const response = await api.get('/display-devices/licence-pdf', {
        params: { format: 'pdf' },
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      throw await asBlobError(err);
    }
  },
  /**
   * Stream the displayer's own licence-application form (GO Ms.No.253
   * Annexure-I — "Application for License/Renewal of License as Displayer")
   * and trigger a browser download. Available at any licence status.
   */
  downloadLicenceApplicationPdf: async (filename = 'Displayer-Application.pdf', type?: 'FRESH' | 'RENEWAL'): Promise<void> => {
    try {
      const response = await api.get('/display-devices/licence-application', {
        params: { format: 'pdf', ...(type ? { type } : {}) },
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      throw await asBlobError(err);
    }
  },

  /**
   * Fetch a PDF endpoint as a `blob:` object URL for the in-page
   * PdfPreviewModal (no save-as, no new tab). Unwraps a JSON/text error body
   * the same way the download* helpers do, so callers get a readable message.
   * The usePdfPreview hook revokes the returned URL on close.
   */
  pdfBlobUrl: async (request: () => Promise<{ data: any }>): Promise<string> => {
    try {
      const response = await request();
      const data = response.data;
      // Preserve the server's real content type. When Puppeteer is unavailable
      // the PDF endpoints fall back to sending the document as HTML; forcing
      // `application/pdf` here made the preview iframe try to render that HTML
      // as a PDF and show the browser's "Failed to load PDF" error. Keeping the
      // blob's own type (text/html for the fallback) renders the receipt inline
      // instead, while a real PDF response still previews as a PDF.
      const blob = data instanceof Blob && data.type
        ? data
        : new Blob([data], { type: 'application/pdf' });
      return window.URL.createObjectURL(blob);
    } catch (err: any) {
      throw await asBlobError(err);
    }
  },

  /**
   * Fetch an uploaded enclosure (Aadhaar, PAN, address proof, structural
   * certificate …) as a `blob:` object URL, so it can be shown inside the
   * portal's own preview modal instead of navigating the browser away to the
   * raw file URL in a fresh tab.
   *
   * Enclosure paths are stored as same-origin static paths
   * (`/apiv1/dsply-uploads/<owner>/<file>`), NOT as ds-vendor API routes — so
   * this deliberately bypasses the `api` instance, whose baseURL would prefix
   * them into a 404. Caller (or the usePdfPreview hook) revokes the URL.
   */
  getUploadedFileObjectUrl: async (path: string): Promise<string> => {
    if (!path) throw new Error('This document has no file on record.');
    try {
      const response = await axios.get(path, { responseType: 'blob' });
      const data = response.data;
      if (data instanceof Blob && data.type) return window.URL.createObjectURL(data);
      const type = response.headers?.['content-type'] || 'application/octet-stream';
      return window.URL.createObjectURL(new Blob([data], { type }));
    } catch (err: any) {
      if (err?.response?.status === 404) {
        throw new Error('This file is no longer available on the server.');
      }
      throw new Error(err?.message || 'Failed to load the document.');
    }
  },

  /** Device Annexure (application/order) PDF as an object URL for in-page preview. */
  getDeviceAnnexureObjectUrl: (dvceId: number, kind: 'application' | 'order', type?: 'FRESH' | 'RENEWAL', dsplrId?: number): Promise<string> =>
    displayDevicesApi.pdfBlobUrl(() =>
      api.get(`/display-devices/devices/${dvceId}/annexure/${kind}`, {
        params: { format: 'pdf', ...(type ? { type } : {}), ...scoped(dsplrId) },
        responseType: 'blob',
      }),
    ),

  /**
   * Printable QR tag for one device, as an object URL for in-page preview.
   *
   * The displayer prints this and pastes it on the erected structure; an
   * inspecting officer scans it in the field app to pull up the device record.
   * The server issues the token on the first request and returns the SAME one
   * on every reprint, so replacing a defaced sticker does not change the
   * device's identity. Gated on ACTIVE — the server returns 400 otherwise.
   */
  getDeviceQrStickerObjectUrl: (dvceId: number, dsplrId?: number): Promise<string> =>
    displayDevicesApi.pdfBlobUrl(() =>
      api.get(`/display-devices/devices/${dvceId}/qr-sticker`, {
        params: { format: 'pdf', ...scoped(dsplrId) },
        responseType: 'blob',
      }),
    ),

  /**
   * Save the QR tag PDF to disk. Built on pdfBlobUrl so the JSON/text error
   * bodies the server returns for a non-ACTIVE device or a foreign dvce_id
   * surface as readable messages rather than a corrupt download.
   */
  downloadDeviceQrSticker: async (dvceId: number, filename?: string, dsplrId?: number): Promise<void> => {
    const url = await displayDevicesApi.getDeviceQrStickerObjectUrl(dvceId, dsplrId);
    try {
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || `QR-Tag-${dvceId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } finally {
      window.URL.revokeObjectURL(url);
    }
  },

  /** Annexure-II displayer licence PDF as an object URL for in-page preview. */
  getLicenceObjectUrl: (): Promise<string> =>
    displayDevicesApi.pdfBlobUrl(() =>
      api.get('/display-devices/licence-pdf', { params: { format: 'pdf' }, responseType: 'blob' }),
    ),

  /**
   * Every term this account's licence (DSPLR) or one of its devices (DVCE) has
   * held, newest first — GO Ms.No.253 §16(b)(ii).
   *
   * The licence record itself only carries the CURRENT window and a single
   * signed-certificate pointer, which each renewal overwrites. So without this
   * the holder cannot retrieve the certificate they were operating under last
   * term — the one an inspection or a deposit-release query is about.
   * Server-side the list is scoped to the caller's own records.
   */
  getIssuedTerms: async (kind: 'DSPLR' | 'DVCE', enttyId: number, dsplrId?: number): Promise<any[]> => {
    const res = await api.get(`/display-devices/terms/${kind}/${enttyId}`, { params: scoped(dsplrId) });
    const body: any = res?.data;
    return Array.isArray(body?.data) ? body.data : [];
  },

  /**
   * A PAST term's signed certificate. Same endpoint as the current one with the
   * term pinned, so there is one download path and one definition of what a
   * signed document is. 404s when the term is not this account's.
   */
  downloadTermCertificate: async (termId: number, filename = 'Displayer-Licence.pdf'): Promise<void> => {
    try {
      const response = await api.get('/display-devices/licence-pdf', {
        params: { format: 'pdf', term: termId },
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      throw await asBlobError(err);
    }
  },

  /** Annexure-I licence-application form PDF as an object URL for in-page preview. */
  getLicenceApplicationObjectUrl: (type?: 'FRESH' | 'RENEWAL'): Promise<string> =>
    displayDevicesApi.pdfBlobUrl(() =>
      api.get('/display-devices/licence-application', {
        params: { format: 'pdf', ...(type ? { type } : {}) },
        responseType: 'blob',
      }),
    ),

  /** Tax-invoice PDF as an object URL for in-page preview. */
  getInvoiceObjectUrl: (type: 'licence-fee' | 'security-deposit' | 'device-fee', dvce_id?: number): Promise<string> =>
    displayDevicesApi.pdfBlobUrl(() =>
      api.get('/display-devices/invoice', { params: { type, format: 'pdf', dvce_id }, responseType: 'blob' }),
    ),

  /** Receipt PDF as an object URL for in-page preview. */
  getReceiptObjectUrl: (rcptId: number): Promise<string> =>
    displayDevicesApi.pdfBlobUrl(() =>
      api.get(`/display-devices/receipts/${rcptId}/pdf`, { params: { format: 'pdf' }, responseType: 'blob' }),
    ),

  /** Notice PDF as an object URL for in-page preview. */
  getNoticeObjectUrl: (noticeId: number): Promise<string> =>
    displayDevicesApi.pdfBlobUrl(() =>
      api.get(`/display-devices/notices/${noticeId}/pdf`, { params: { format: 'pdf' }, responseType: 'blob' }),
    ),

  /**
   * Penalty PDF as an object URL for in-page preview. `dsplrId` scopes the
   * request to the owning licence, the way getPenaltyHtml does, so the
   * consolidated All-ULBs list can open a penalty that belongs to a sibling
   * licence rather than the one currently switched to.
   */
  getPenaltyObjectUrl: (penaltyId: number, dsplrId?: number): Promise<string> =>
    displayDevicesApi.pdfBlobUrl(() =>
      api.get(`/display-devices/penalties/${penaltyId}/pdf`, { params: { format: 'pdf', ...scoped(dsplrId) }, responseType: 'blob' }),
    ),

  /**
   * Upload an apply-time enclosure document (GO Ms.No.253 Annexure-III / V).
   * Returns the stored URL and metadata — attach to the device apply payload.
   */
  uploadEnclosure: async (
    file: File,
    dcmtTypeCd: string,
  ): Promise<{ dcmt_type_cd: string; dcmt_path_tx: string; file_nm_tx: string; mime_tx: string; sz_kb_nu: number }> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('dcmt_type_cd', dcmtTypeCd);
    const response = await api.post('/display-devices/devices/upload-enclosure', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.data;
  },

  /**
   * Retrieve uploaded enclosures for a device (Annexure-III / V documents).
   */
  getDeviceDocuments: async (dvceId: number, dsplrId?: number): Promise<any[]> => {
    const response = await api.get(`/display-devices/devices/${dvceId}/documents`, { params: scoped(dsplrId) });
    return response.data.data || [];
  },

  /* ---- Display Content History (GO Ms.No.253 Rule 18) ----------------- */

  /** Content history (what's displaying) logged for one of the vendor's devices. */
  getDeviceContent: async (dvceId: number, dsplrId?: number): Promise<ContentHistoryRow[]> => {
    const response = await api.get(`/display-devices/devices/${dvceId}/content`, { params: scoped(dsplrId) });
    return response.data.data || [];
  },

  /** Upload a single content photo (JPG/PNG). Returns the stored URL + metadata. */
  uploadContentPhoto: async (
    file: File,
  ): Promise<{ dcmt_path_tx: string; file_nm_tx: string; mime_tx: string; sz_kb_nu: number }> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/display-devices/devices/upload-content-photo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.data;
  },

  /** Upload a single content video (MP4/WebM/OGG/MOV/AVI/MKV, up to 100 MB). */
  uploadContentVideo: async (
    file: File,
  ): Promise<{ dcmt_path_tx: string; file_nm_tx: string; mime_tx: string; sz_kb_nu: number }> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/display-devices/devices/upload-content-video', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.data;
  },

  /** Log a new display-content entry for an ACTIVE device. */
  createDeviceContent: async (dvceId: number, payload: CreateContentPayload): Promise<ContentHistoryRow> => {
    const response = await api.post(`/display-devices/devices/${dvceId}/content`, payload);
    return response.data.data;
  },

  /** Withdraw one of the vendor's own ACTIVE content entries. */
  withdrawDeviceContent: async (dvceId: number, cntntId: number): Promise<any> => {
    const response = await api.post(`/display-devices/devices/${dvceId}/content/${cntntId}/withdraw`);
    return response.data.data;
  },

  /**
   * Retrieve the licence-application enclosures (Annexure-I / §6(II)) the
   * displayer uploaded when applying for their Displayer Licence.
   */
  getDisplayerDocuments: async (): Promise<any[]> => {
    const response = await api.get('/display-devices/licence/documents');
    return response.data.data || [];
  },

  /**
   * Partners + enclosures of the account's PRIMARY licence, used to prefill an
   * "apply in another ULB" form (GO §6 — Professional per-ULB reuse). Always
   * anchored to the primary, so every subsequent ULB application reuses the same
   * canonical set.
   */
  /**
   * GO Rule 6(I)(A) — can this Individual licence upgrade to Professional, and
   * what does it cost? `quote` carries the deposit carry-forward tally
   * (dpst_full_at / dpst_crdt_at / dpst_pybl_at) so the form can show the ULB's
   * published Professional deposit AND the shortfall actually payable, rather
   * than one unexplained number.
   */
  getUpgradePreview: async (): Promise<{
    current: {
      dsplr_id: number; dsplr_type_cd: string; dsplr_type_nm?: string;
      lcnse_nu_tx?: string; ulb_id?: number; ulb_nm?: string; sts_cd?: string;
      scrty_dpst_at?: number;
    };
    canUpgrade: boolean;
    blockers: string[];
    quote: {
      lcnse_fee_at: number; gst_at: number; gst_pct: number;
      dpst_full_at: number; dpst_crdt_at: number; dpst_pybl_at: number;
      dpst_crdt_src: string; ttl_pybl_at: number;
    } | null;
    quoteMessage: string | null;
    deviceCounts: Array<{ ulb_id: number; count: number }>;
    deviceTotal: number;
    individualDeviceCap: number;
    openUpgrade: any | null;
  }> => {
    const response = await api.get('/display-devices/upgrade/preview');
    return response.data.data;
  },

  getLicenceReuseProfile: async (): Promise<{ partners: any[]; documents: any[] }> => {
    const response = await api.get('/display-devices/licence/profile-for-reuse');
    return response.data.data || { partners: [], documents: [] };
  },

  /**
   * Resubmit a Displayer-Licence application the officer returned for corrected
   * documents (workflow state RETURNED_TO_APPLICANT). Sends only the re-uploaded
   * files, keyed by doc-type code; the server replaces the rejected enclosures,
   * resets them to 'pending', and pushes the application back to the reviewer.
   */
  resubmitLicence: async (
    dsplrId: number,
    docFiles: Record<string, File | null>,
  ): Promise<{ dsplr_id: number; replaced: number }> => {
    const fd = new FormData();
    for (const [code, file] of Object.entries(docFiles)) {
      if (file) fd.append(String(code).toUpperCase(), file, file.name);
    }
    const response = await api.post(
      `/display-devices/licence/resubmit?dsplr_id=${dsplrId}`,
      fd,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response.data?.data;
  },

  /**
   * Resubmit a Device-Permission application the officer returned for corrected
   * documents (device workflow state RETURNED_TO_APPLICANT). Device analogue of
   * resubmitLicence — sends only the re-uploaded files, keyed by doc-type code;
   * the server replaces the rejected device enclosures, resets them to 'pending',
   * and pushes the device application back to the reviewer (WPRS_REVIEW).
   */
  resubmitDevice: async (
    dvceId: number,
    docFiles: Record<string, File | null>,
  ): Promise<{ dvce_id: number; replaced: number }> => {
    const fd = new FormData();
    for (const [code, file] of Object.entries(docFiles)) {
      if (file) fd.append(String(code).toUpperCase(), file, file.name);
    }
    const response = await api.post(
      `/display-devices/devices/${dvceId}/resubmit`,
      fd,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response.data?.data;
  },

  /**
   * Apply for a Displayer Licence from inside the portal (GO Ms.No.253
   * §13(c)–(d)). Upgrades a TEMPORARY_APPLICANT to DISPLAYER.
   *
   * Always sends multipart/form-data so the Annexure-I / §6(II) document
   * uploads (PAN, Aadhaar, GST, IT returns, etc.) ride along with the JSON
   * body. The server reads `displayer_license` as a JSON string field and
   * `req.files` for the attachments, using each file's fieldname as the
   * doc-type code so it can FK to dsply_dcmt_type_lst_t.
   */
  applyLicence: async (
    payload: { displayer_license: Record<string, any> },
    docFiles?: Record<string, File | null>,
  ): Promise<any> => {
    const fd = new FormData();
    fd.append('displayer_license', JSON.stringify(payload.displayer_license));
    if (docFiles) {
      for (const [code, file] of Object.entries(docFiles)) {
        if (file) fd.append(String(code).toUpperCase(), file, file.name);
      }
    }
    const response = await api.post('/display-devices/licence/apply', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    // If the server returns a fresh JWT, update local storage so subsequent
    // requests carry the upgraded entity context immediately.
    const data = response.data?.data;
    if (data?.token) {
      localStorage.setItem('vendor_token', data.token);
    }
    if (data?.user) {
      localStorage.setItem('vendor_user', JSON.stringify(data.user));
    }
    // Entity context may have changed (first licence) — drop the licence scope
    // so the next Display Devices load re-derives from the (primary) account.
    activeDsplrId = null;
    return data;
  },

  /**
   * Licence cancellation (GO Rule 21-c Note(b) + Rule 21-e refund) —
   * eligibility preview consumed by the Cancel Licence modal.
   */
  getLicenceCancelEligibility: async (): Promise<{
    eligible: boolean;
    reason?: string;
    message?: string;
    licence?: any;
    pending_dues?: number;
    active_children?: number;
    days_to_expiry?: number;
    penalty_applicable?: boolean;
    estimated_penalty_at?: number;
    security_deposit_at?: number;
    estimated_refund_at?: number;
    refund_eligible_dt_after_approval_days?: number;
  }> => {
    const response = await api.get('/display-devices/licence/cancel-eligibility');
    return response.data.data;
  },

  /** Submit a licence cancellation request. Server re-validates eligibility. */
  requestLicenceCancellation: async (payload: {
    grnd_cd: string;
    grnd_desc_tx?: string;
    cncl_rsn_tx?: string;
    rfnd_bnk_acct_nm: string;
    rfnd_bnk_acct_nu: string;
    rfnd_bnk_ifsc_cd: string;
    rfnd_bnk_nm?: string;
    rfnd_bnk_brnch_tx?: string;
  }): Promise<{
    lcnse_cncl_id: number;
    estimated_penalty_at: number;
    estimated_refund_at: number;
    days_to_expiry: number;
    refund_eligible_after_days: number;
  }> => {
    const response = await api.post('/display-devices/licence/cancel-request', payload);
    return response.data.data;
  },

  /**
   * Status of a cancellation already submitted: the request, the desk it is
   * sitting on, and — once approved — the security-deposit refund claim with
   * its six-month countdown and the receipts the amount was derived from.
   *
   * Same stored figures the ULB officer's payout screen reads. A deposit this
   * size showing one number in the portal and another at the counter is how
   * disputes start, so both sides render the same row.
   */
  getLicenceCancellationStatus: async (): Promise<{
    cancellation: {
      lcnse_cncl_id: number; grnd_cd: string; grnd_desc_tx?: string | null;
      cncl_rsn_tx?: string | null; sts_cd: string;
      pnlty_at: number; pndng_dues_at: number; rfnd_at: number;
      rfnd_bnk_acct_nm?: string | null; rfnd_bnk_acct_nu?: string | null;
      rfnd_bnk_ifsc_cd?: string | null; rfnd_bnk_nm?: string | null;
      rfnd_bnk_brnch_tx?: string | null;
      officer_nm?: string | null; cscde_dvce_ct?: number | null;
      rqstd_on_fmt?: string | null;
      refund_eligible_dt_fmt?: string | null;
      refund_paid_dt_fmt?: string | null; refund_rcpt_tx?: string | null;
    } | null;
    workflow: {
      wrkflw_inst_id: number; ref_nu_tx: string; sts_nu: number;
      state_cd?: string | null; state_nm?: string | null; sqnce_nu?: number | null;
      strt_dt_fmt?: string | null;
    } | null;
    refund: {
      rfnd_id: number; rfnd_nu_tx: string; sts_cd: string;
      gross_dpst_at: number; pnlty_at: number; dues_at: number; net_rfnd_at: number;
      fnl_dues_at?: number | null; fnl_rfnd_at?: number | null;
      elgble_frm_dt_fmt?: string | null; days_to_maturity?: number | null;
      rfnd_paid_dt_fmt?: string | null; pay_mode_cd?: string | null;
      pay_ref_tx?: string | null; rfnd_rcpt_tx?: string | null;
      rfnd_bnk_acct_nu?: string | null; rfnd_bnk_ifsc_cd?: string | null;
      rfnd_bnk_nm?: string | null; hold_rsn_tx?: string | null;
      /** GO Rule 21(e)(iii) forfeiture — the only reduction the GO permits. */
      frft_at?: number | null; frft_gst_at?: number | null;
      frft_rsn_cd?: string | null; frft_rsn_tx?: string | null;
      /** Dues the ULB chose to recover here. Not a GO deduction — see Rule 23. */
      dues_adjstd_at?: number | null; dues_adjstd_rsn_tx?: string | null;
    } | null;
    refund_sources: Array<{
      dmnd_nu_tx?: string | null; dmnd_dt_fmt?: string | null;
      clctd_at: number; rcpt_tx?: string | null;
    }>;
  }> => {
    const response = await api.get('/display-devices/licence/cancellation-status');
    return response.data.data;
  },

  /**
   * Quote the displayer-licence renewal under GO §a(i) + §c. Server returns
   * mode (RENEW or REAPPLY), tier %, base/late/GST/total and the new
   * validity window. Pure read — call freely to drive UI state.
   */
  getRenewalQuote: async (claimsPpp?: boolean): Promise<RenewalQuote> => {
    // GO Rule 15(e) — the concession is re-quoted only when the applicant ticks
    // the claim on THIS renewal. Without the flag the server returns the full
    // Annexure-VII tariff; a previous term's concession never carries into it.
    const response = await api.get('/display-devices/renewal-quote', {
      params: claimsPpp ? { ppp_in: true } : undefined,
    });
    return response.data.data as RenewalQuote;
  },

  /**
   * Submit the renewal / re-application. Server re-runs the quote so client
   * totals are advisory only. Returns the created renewal id and final mode.
   * The `payment` block carries the simulated payment fields the modal
   * collected (mode, ref no, paid_on) so DcbService can mint a receipt.
   */
  submitRenewal: async (
    payment?: SimulatedPayment,
    // GO Rule 15(e) — this term's own claim. Sending ppp_in requires BOTH the
    // council resolution number and a fresh PPP_PROOF enclosure, or the server
    // rejects: the concession rewards a continuing obligation, so last term's
    // proof cannot answer whether it still holds.
    ppp?: {
      ppp_in: boolean;
      ppp_cncl_aprvl_tx?: string;
      enclosures?: Array<{ dcmt_type_cd: string; dcmt_path_tx: string; file_nm_tx?: string }>;
    },
  ): Promise<RenewalSubmitResult> => {
    const response = await api.post('/display-devices/renew', { payment: payment || null, ...(ppp || {}) });
    return response.data.data as RenewalSubmitResult;
  },

  // ─── Receipts (shared DCB ledger) ──────────────────────────────────────
  /** List receipts for the logged-in displayer. */
  listReceipts: async (params: { limit?: number; offset?: number; search?: string; dsplr_id?: number } = {}): Promise<ReceiptListResponse> => {
    const response = await api.get('/display-devices/receipts', { params });
    return response.data.data as ReceiptListResponse;
  },

  /**
   * URL the browser can open / `<a download>` to fetch the printable PDF.
   * No JSON wrapper — the response is the PDF body itself.
   */
  receiptPdfUrl: (rcptId: number): string =>
    `${BASE_URL}/display-devices/receipts/${rcptId}/pdf?format=pdf`,

  /**
   * Trigger the browser download for a receipt PDF. Adds the bearer token
   * via a fetch then drops the blob into a hidden <a> click — works
   * around the fact that <a href> can't carry an Authorization header.
   */
  downloadReceiptPdf: async (rcptId: number, fileName?: string): Promise<void> => {
    const token = localStorage.getItem('vendor_token') || '';
    const r = await fetch(displayDevicesApi.receiptPdfUrl(rcptId), {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!r.ok) {
      // Surface the server's own sentence ("Receipt is not printable yet", …)
      // rather than a bare status code — this is a plain fetch, so nothing
      // unwraps the JSON error body for us.
      const text = await r.text().catch(() => '');
      let message = '';
      try { message = String(JSON.parse(text)?.message || ''); } catch { /* not JSON */ }
      throw new Error(message || `Failed to fetch receipt (${r.status})`);
    }
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName || `receipt-${rcptId}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  },

  // ─── Displayer Type Change (Individual ↔ Professional) ─────────────────
  // Auto-approve: payment success directly flips the displayer record.
  // No JA→SA→MC workflow.

  /** Whether the displayer can currently upgrade or downgrade + the blockers if not. */
  getTypeChangeEligibility: async (): Promise<{
    current: { dsplr_type_id: number; dsplr_type_cd: 'INDIVIDUAL' | 'PROFESSIONAL'; dsplr_type_nm: string };
    canUpgrade: boolean;
    canDowngrade: boolean;
    blockers: string[];
    downgradeBlockers: string[];
    /** Whether the server currently offers Professional → Individual at all.
     *  GO Ms.No.253 has no downgrade route, so this is normally false and the
     *  submit endpoint 403s. Optional: older servers omit it.
     *  See lib/displayerTypePolicy.ts. */
    downgradeEnabled?: boolean;
    downgradeDisabledReason?: string | null;
    pendingRequest: any | null;
    deviceCounts: Array<{ ulb_id: number; count: number }>;
  }> => {
    const response = await api.get('/display-devices/type-change/eligibility');
    return response.data.data;
  },

  /** Fee differential preview for the target type. */
  getTypeChangeQuote: async (to: 'INDIVIDUAL' | 'PROFESSIONAL'): Promise<any> => {
    const response = await api.get('/display-devices/type-change/quote', { params: { to } });
    return response.data.data;
  },

  /**
   * Submit a type-change request. Collects the fee differential, writes the
   * DCB receipt, and enqueues a DISPLAYER_TYPE_CHANGE workflow instance.
   * The actual change to the displayer record only happens after the ULB
   * officer approves the workflow.
   *
   * Body is a JSON string under `type_change` (now including `payment`),
   * plus file fields keyed by document-type code (GST_CERT / PARTNERSHIP_DEED
   * / etc.).
   */
  applyTypeChange: async (
    payload: {
      to_dsplr_type_cd: 'INDIVIDUAL' | 'PROFESSIONAL';
      to_entty_type_id?: number;
      to_entty_nm?: string;
      to_gstr_tx?: string;
      partners?: Array<{ nm: string; pan?: string; share_pct?: number }>;
      rmrks_tx?: string;
      payment?: SimulatedPayment;
    },
    files?: Array<{ field: string; file: File }>,
  ): Promise<{
    req_id: number;
    wrkflw_inst_id: number | null;
    rcpt_id: number | null;
    rcpt_nu_tx: string | null;
    total: number;
    quote: any;
  }> => {
    const form = new FormData();
    form.append('type_change', JSON.stringify(payload));
    (files || []).forEach((f) => form.append(f.field, f.file, f.file.name));
    const response = await api.post('/display-devices/type-change/apply', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.data;
  },

  /** Cancel a PENDING type-change request before paying. */
  withdrawTypeChange: async (reqId: number): Promise<void> => {
    await api.post(`/display-devices/type-change/${reqId}/withdraw`, {});
  },

  /** Full detail for a single type-change request (status, fees, receipt). */
  getTypeChangeRequest: async (reqId: number): Promise<any> => {
    const response = await api.get(`/display-devices/type-change/${reqId}`);
    return response.data.data;
  },
};

/** Payment fields the modal collects before submit — passed to DcbService. */
export interface SimulatedPayment {
  mthdCd: 'CASH' | 'CHEQUE' | 'DD' | 'UPI' | 'NEFT' | 'RTGS' | 'ONLINE';
  instrumentNo?: string | null;
  instrumentDt?: string | null;
  bankNm?: string | null;
  paid_on?: string | null;
}

export interface ReceiptListRow {
  rcpt_id: number;
  rcpt_nu_tx: string;
  ref_type_cd: 'DISPLAYER' | 'DEVICE' | 'RENEWAL' | 'PENALTY' | 'TYPE_CHANGE';
  ref_id: number;
  cnsmer_nm: string | null;
  ttl_at: number;
  gst_at: number;
  net_at: number;
  pymnt_mde_cd: string;
  instrmnt_nu_tx: string | null;
  sts_cd: string;
  rcpt_dt_fmt: string;
  rcpt_dt: string;
  ulb_nm: string | null;
  dvce_unque_id_tx: string | null;
}

export interface ReceiptListResponse {
  total: number;
  rows: ReceiptListRow[];
  limit: number;
  offset: number;
}

export interface PendingRenewal {
  id: number;
  status: string;
  total: number;
  renewalFee: number;
  lateFeeAmount: number;
  gstAmount: number;
  createdAt: string;
  demandId: number | null;
  demandTotal: number;
  demandCollected: number;
  demandBalance: number;
  paid: boolean;
}

export interface RenewalQuote {
  mode: 'RENEW' | 'REAPPLY';
  state: 'Active' | 'RenewalDue' | 'Late' | 'Terminated';
  displayerId: number;
  licenceNumber: string | null;
  currentValidFrom: string | null;
  currentValidTo: string;
  applicationDate: string;
  daysFromExpiry: number;
  baseFee: number;
  lateFeePct: 0 | 25 | 50 | 100;
  lateFeeAmount: number;
  subtotal: number;
  gstRate: number;
  gstAmount: number;
  total: number;
  newValidFrom: string;
  newValidTo: string;
  goClause: string;
  windowLabel: string;
  securityDepositCollected: boolean;
  windowOpen: boolean;
  pendingRenewal: PendingRenewal | null;
}

/** Compact projection that summary + detail include for the dashboard card / hero KPI. */
export interface ProjectedRenewal {
  status: 'PROJECTED' | 'PENDING_PAYMENT' | 'AWAITING_APPROVAL' | 'NONE';
  amountDue: number;
  mode: 'RENEW' | 'REAPPLY';
  lateFeePct: number;
  baseFee: number;
  lateFeeAmount: number;
  gstAmount: number;
  total: number;
  windowLabel: string;
  goClause: string;
  currentValidTo: string;
  pendingRenewalId: number | null;
  windowOpen: boolean;
}

export interface RenewalSubmitResult {
  renewalId: number | null;
  mode: 'RENEW' | 'REAPPLY';
  total: number;
  lateFeePct: number;
  newValidTo: string;
}

// ========== Notifications API ==========
// In-app notifications for the bell icon and dashboard banner.
// Currently surfaces TYPE_CHANGE_APPROVED / TYPE_CHANGE_REJECTED rows emitted
// by the server when the Commissioner workflow completes.
export type VendorNotificationKind =
  | 'TYPE_CHANGE_APPROVED'
  | 'TYPE_CHANGE_REJECTED'
  | 'LICENCE_APPROVED'
  | 'LICENCE_REJECTED'
  | 'DEVICE_APPLICATION_APPROVED'
  | 'DEVICE_APPLICATION_REJECTED'
  | 'LICENCE_CANCEL_APPROVED'
  | 'LICENCE_CANCEL_REJECTED'
  | 'NOTICE_ISSUED'
  | 'PENALTY_ISSUED'
  | 'PENALTY_ISSUED_WITH_CANCEL'
  // What an inspection now tells the displayer: the visit and its result, what
  // has to be put right, and a warning before the next one falls due.
  // INSPECTION_SCHEDULED is kept only so notifications already in the bell from
  // before the redesign still render with an inspection badge.
  | 'INSPECTION_RECORDED'
  | 'INSPECTION_ACTION_REQUIRED'
  | 'INSPECTION_DUE_SOON'
  | 'INSPECTION_SCHEDULED'
  | 'PAYMENT_DUE'
  | 'APPLICATION_RETURNED'
  | string;

export interface VendorNotification {
  ntfctn_id: number;
  dsplr_id: number;
  kind_cd: VendorNotificationKind;
  ttl_tx: string;
  body_tx: string;
  meta_jn: any;
  read_in: boolean;
  read_ts: string | null;
  i_ts: string;
}

export interface NotificationListResult {
  items: VendorNotification[];
  unread_count: number;
  limit: number;
  offset: number;
}

export const notificationsApi = {
  list: async (opts: { unread?: boolean; limit?: number; offset?: number } = {}): Promise<NotificationListResult> => {
    const params: Record<string, string | number> = {};
    if (opts.unread) params.unread = 1;
    if (opts.limit  != null) params.limit  = opts.limit;
    if (opts.offset != null) params.offset = opts.offset;
    const response: AxiosResponse<ApiResponse<NotificationListResult>> = await api.get('/notifications', { params });
    return response.data.data;
  },

  unreadCount: async (): Promise<number> => {
    const response: AxiosResponse<ApiResponse<{ unread_count: number }>> = await api.get('/notifications/unread-count');
    return Number(response.data.data?.unread_count || 0);
  },

  /**
   * The Socket.IO room + event name for this displayer's live signals. The room
   * is an HMAC the server derives from the caller's own displayer id, so this
   * authenticated call is the only way to obtain it (see vendorRealtime.ts).
   */
  realtimeChannel: async (): Promise<{ room: string; event: string }> => {
    const response: AxiosResponse<ApiResponse<{ room: string; event: string }>> = await api.get('/notifications/realtime');
    return response.data.data;
  },

  markRead: async (ntfctnId: number): Promise<boolean> => {
    const response: AxiosResponse<ApiResponse<{ updated: boolean }>> = await api.post(`/notifications/${ntfctnId}/read`);
    return Boolean(response.data.data?.updated);
  },

  markAllRead: async (): Promise<number> => {
    const response: AxiosResponse<ApiResponse<{ updated: number }>> = await api.post('/notifications/read-all');
    return Number(response.data.data?.updated || 0);
  },
};

export default api;
